// Edit Role Page - Roles & Permissions Management
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2, Loader2, Clock, FileText, CheckSquare, Settings, BarChart3, User, Users, ArrowRight, Mail, LayoutDashboard, ClipboardList, Sparkles, ClipboardCheck, FileBarChart, Plug, Building2, AlertCircle, Settings2, ChevronDown, Square, Lock, Plus, Minus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { PageHeader } from '@/components/blocks/page-header';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { useGlobalSettings } from '@/hooks/use-global-settings';
import { useDataSource } from '@/contexts/DataSourceContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { getUserRoleService, type UserRole, type UserRolePermissions, type CreateUserRoleDto, type UpdateUserRoleDto, type UserRoleListItem } from '@/services/userRoleService';
import { ApiError } from '@/services/apiClient';
import { DEFAULT_ROLE_PERMISSIONS_MAP } from '@/services/defaultRolePermissions';
import { prefixKey } from '@/utils/storagePrefix';
import { useUnsavedChanges } from '@/contexts/UnsavedChangesContext';

interface EditRolePageProps {
  onNavigate?: (path: string) => void;
}

interface Permission {
  id: string;
  name: string;
  description: string;
}

interface PermissionGroup {
  name: string;
  permissions: Permission[];
}

interface PermissionCategory {
  id: string;
  name: string;
  description: string;
  iconName: 'LayoutDashboard' | 'Clock' | 'FileText' | 'CheckSquare' | 'BarChart3' | 'Settings';
  iconColor: string;
  scope: 'basic' | 'management' | 'admin' | 'advanced';
  permissions: Permission[];
  groups?: PermissionGroup[];
}

const getScopeColor = (scope: 'basic' | 'management' | 'admin' | 'advanced') => {
  switch (scope) {
    case 'basic':
      return 'bg-success-100 text-success-600 dark:bg-success-900/20 dark:text-success-400';
    case 'management':
      return 'bg-info-100 text-info-600 dark:bg-info-900/20 dark:text-info-400';
    case 'admin':
      return 'bg-burntorange-100 text-burntorange-600 dark:bg-burntorange-900/20 dark:text-burntorange-400';
    case 'advanced':
      return 'bg-blueviolet-100 text-blueviolet-600 dark:bg-blueviolet-900/20 dark:text-blueviolet-400';
    default:
      return 'bg-neutral-100 text-neutral-600 dark:bg-neutral-900/20 dark:text-neutral-400';
  }
};

// Helper to render icons
const getIcon = (iconName: string, className?: string) => {
  const iconProps = { className: className || "h-5 w-5" };
  switch (iconName) {
    case 'LayoutDashboard': return <LayoutDashboard {...iconProps} />;
    case 'Clock': return <Clock {...iconProps} />;
    case 'FileText': return <FileText {...iconProps} />;
    case 'CheckSquare': return <CheckSquare {...iconProps} />;
    case 'BarChart3': return <BarChart3 {...iconProps} />;
    case 'Settings': return <Settings {...iconProps} />;
    default: return null;
  }
};

const permissionCategories: PermissionCategory[] = [
  {
    id: 'dashboard',
    name: 'Dashboard Permissions',
    description: 'Access to dashboard views and metrics',
    iconName: 'LayoutDashboard',
    iconColor: '',
    scope: 'basic',
    permissions: [
      { id: 'dashboard.view_own', name: 'View Own Dashboard', description: 'View personal dashboard with own metrics and action items' },
      { id: 'dashboard.view_team', name: 'View Team Dashboard', description: 'View team dashboard with team member metrics' },
      { id: 'dashboard.view_department', name: 'View Department Dashboard', description: 'View department-wide dashboard with aggregated metrics' },
    ],
  },
  {
    id: 'timesheet',
    name: 'Timesheet Permissions',
    description: 'Manage timesheet entry and tracking capabilities',
    iconName: 'Clock',
    iconColor: '',
    scope: 'basic',
    groups: [
      {
        name: 'View Access',
        permissions: [
          { id: 'timesheet.view_own', name: 'View Own Timesheets', description: 'View and access personal timesheet entries' },
          { id: 'timesheet.view_team', name: 'View Team Timesheets', description: 'View timesheet entries for direct reports' },
          { id: 'timesheet.view_all', name: 'View All Timesheets', description: 'View any employee\'s timesheet entries' },
        ],
      },
      {
        name: 'Entry Management',
        permissions: [
          { id: 'timesheet.create', name: 'Create Time Entries', description: 'Add new time entries to timesheets' },
          { id: 'timesheet.edit_own', name: 'Edit Own Entries', description: 'Modify personal time entries (status-dependent)' },
          { id: 'timesheet.edit_team', name: 'Edit Team Entries', description: 'Modify team member time entries (limited)' },
          { id: 'timesheet.edit_all', name: 'Edit All Entries', description: 'Modify any timesheet entry (admin override)' },
          { id: 'timesheet.delete_own', name: 'Delete Own Entries', description: 'Remove personal time entries' },
          { id: 'timesheet.delete_team', name: 'Delete Team Entries', description: 'Remove team member time entries' },
        ],
      },
      {
        name: 'Workflow Actions',
        permissions: [
          { id: 'timesheet.submit', name: 'Submit Timesheet', description: 'Submit timesheet for approval' },
          { id: 'timesheet.recall', name: 'Recall Timesheet', description: 'Recall a submitted timesheet' },
          { id: 'timesheet.copy', name: 'Copy Timesheet', description: 'Copy entries from previous or selected weeks' },
          { id: 'timesheet.view_audit', name: 'View Audit History', description: 'View timesheet change history and audit trail' },
        ],
      },
    ],
    permissions: [],
  },
  {
    id: 'approvals',
    name: 'Approval Permissions',
    description: 'Manage timesheet approval workflows',
    iconName: 'CheckSquare',
    iconColor: '',
    scope: 'management',
    permissions: [
      { id: 'approvals.view_queue', name: 'View Approval Queue', description: 'Access the approval dashboard and pending queue' },
      { id: 'approvals.approve', name: 'Approve Timesheets', description: 'Approve submitted timesheet entries' },
      { id: 'approvals.reject', name: 'Reject Timesheets', description: 'Reject timesheets with reason' },
      { id: 'approvals.bulk_approve', name: 'Bulk Approve & Reject', description: 'Approve or reject multiple timesheets at once' },
      { id: 'approvals.view_details', name: 'View Approval Details', description: 'View detailed timesheet information during approval' },
      { id: 'approvals.view_automation', name: 'View Automation Suggestions', description: 'View AI-powered automation suggestions' },
      { id: 'approvals.manage_automation', name: 'Manage Automation Rules', description: 'Configure and manage approval automation rules' },
    ],
  },
  {
    id: 'reports',
    name: 'Reports & Analytics Permissions',
    description: 'Access reporting and analytics features',
    iconName: 'BarChart3',
    iconColor: '',
    scope: 'management',
    groups: [
      {
        name: 'Standard Reports',
        permissions: [
          { id: 'reports.view_own', name: 'View Own Reports', description: 'View personal timesheet and utilization reports' },
          { id: 'reports.view_team', name: 'View Team Reports', description: 'View reports for direct reports' },
          { id: 'reports.view_department', name: 'View Department Reports', description: 'View reports for entire department' },
          { id: 'reports.view_all', name: 'View All Reports', description: 'View organization-wide reports' },
          { id: 'reports.export', name: 'Export Reports', description: 'Export report data to CSV, Excel, or PDF' },
        ],
      },
    ],
    permissions: [],
  },
  {
    id: 'team',
    name: 'Team Management Permissions',
    description: 'Manage team members and their details',
    iconName: 'Settings',
    iconColor: '',
    scope: 'management',
    permissions: [
      { id: 'team.view_directory', name: 'View Employee Directory', description: 'View the employee directory and basic information' },
      { id: 'team.view_details', name: 'View Employee Details', description: 'View detailed employee information and profiles' },

    ],
  },
  {
    id: 'projects',
    name: 'Project Management Permissions',
    description: 'Control project and task management access',
    iconName: 'FileText',
    iconColor: '',
    scope: 'management',
    permissions: [
      { id: 'projects.view_assigned', name: 'View Assigned Projects', description: 'View only projects assigned to the user' },
      { id: 'projects.view_all', name: 'View All Projects', description: 'View all active and archived projects' },
      { id: 'projects.create', name: 'Create Projects', description: 'Create new projects' },
      { id: 'projects.edit', name: 'Edit Projects', description: 'Modify project details and settings' },
      { id: 'projects.delete', name: 'Delete Projects', description: 'Delete projects (with restrictions)' },
      { id: 'projects.archive', name: 'Archive Projects', description: 'Archive completed or inactive projects' },
      { id: 'projects.assign', name: 'Assign Projects', description: 'Assign projects to team members' },
      { id: 'projects.manage_budget', name: 'Manage Project Budgets', description: 'View and manage project budgets and forecasts' },
    ],
  },
  {
    id: 'integrations',
    name: 'Integration Permissions',
    description: 'Manage system integrations and company data',
    iconName: 'Settings',
    iconColor: '',
    scope: 'admin',
    permissions: [
      { id: 'integrations.view', name: 'View Integrations', description: 'View integration configurations and status' },
      { id: 'integrations.manage', name: 'Manage Integrations', description: 'Configure and manage system integrations' },
      { id: 'integrations.sync', name: 'Sync Data', description: 'Trigger manual data synchronization' },
      { id: 'companies.view', name: 'View Companies', description: 'View company list and basic information' },
      { id: 'companies.manage', name: 'Manage Companies', description: 'Create, edit, and manage company settings' },
    ],
  },
  {
    id: 'settings',
    name: 'Settings & Administration Permissions',
    description: 'Configure system settings and manage users',
    iconName: 'Settings',
    iconColor: '',
    scope: 'admin',
    permissions: [
      { id: 'settings.view', name: 'View Settings', description: 'View all settings (read-only access)' },
      { id: 'settings.manage_general', name: 'Manage General Settings', description: 'Configure general application settings' },

      { id: 'settings.manage_deadlines', name: 'Manage Deadlines', description: 'Configure timesheet submission deadlines' },
      { id: 'settings.manage_policies', name: 'Manage Policy Settings', description: 'Configure policy violation rules and monitoring' },
      { id: 'settings.manage_utilization', name: 'Manage Utilization Settings', description: 'Configure utilization targets and thresholds' },
      { id: 'settings.manage_roles', name: 'Manage Roles & Permissions', description: 'Create and manage user roles and permissions' },
      { id: 'settings.manage_approval_workflows', name: 'Manage Approval Workflows', description: 'Configure approval workflows and stages' },
      { id: 'settings.manage_notifications', name: 'Manage Notifications', description: 'Configure notification preferences and rules' },
      { id: 'settings.view_audit', name: 'View Audit Logs', description: 'View system audit logs and change history' },
      { id: 'settings.manage_security', name: 'Manage Security Settings', description: 'Manage security and access control settings' },
    ],
  },
  {
    id: 'data',
    name: 'Data Operations Permissions',
    description: 'Import, export, and bulk data operations',
    iconName: 'Settings',
    iconColor: '',
    scope: 'admin',
    permissions: [
      { id: 'data.import', name: 'Import Data', description: 'Import timesheet and employee data' },
      { id: 'data.export', name: 'Export Data', description: 'Export data to various formats' },
      { id: 'data.bulk_operations', name: 'Bulk Operations', description: 'Perform bulk data operations' },
    ],
  },
];

interface UserData {
  id: string;
  name: string;
  email: string;
  roleId: string;
  roleName: string;
  status: 'active' | 'inactive';
  avatar?: string;
}

const mockUsers: UserData[] = [
  { id: '1', name: 'John Smith', email: 'john.smith@company.com', roleId: '1', roleName: 'Administrator', status: 'active' },
  { id: '2', name: 'Sarah Johnson', email: 'sarah.johnson@company.com', roleId: '1', roleName: 'Administrator', status: 'active' },
  { id: '3', name: 'Michael Chen', email: 'michael.chen@company.com', roleId: '1', roleName: 'Administrator', status: 'active' },
  { id: '4', name: 'Emily Davis', email: 'emily.davis@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '5', name: 'David Wilson', email: 'david.wilson@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '6', name: 'Lisa Anderson', email: 'lisa.anderson@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '7', name: 'James Martinez', email: 'james.martinez@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '8', name: 'Jennifer Taylor', email: 'jennifer.taylor@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '9', name: 'Robert Brown', email: 'robert.brown@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '10', name: 'Maria Garcia', email: 'maria.garcia@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '11', name: 'William Lee', email: 'william.lee@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '12', name: 'Patricia White', email: 'patricia.white@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '13', name: 'Thomas Harris', email: 'thomas.harris@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '14', name: 'Linda Clark', email: 'linda.clark@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '15', name: 'Christopher Lewis', email: 'christopher.lewis@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '16', name: 'Barbara Robinson', email: 'barbara.robinson@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '17', name: 'Daniel Walker', email: 'daniel.walker@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
  { id: '18', name: 'Nancy Hall', email: 'nancy.hall@company.com', roleId: '3', roleName: 'Approver', status: 'active' },
];

const mockRoles = {
  '1': {
    id: '1',
    name: 'Administrator',
    description: 'Full system access and control',
    userCount: 3,
    isSystem: true,
    isActive: true,
    permissions: permissionCategories.flatMap(c => c.permissions.map(p => p.id)),
  },
  '2': {
    id: '2',
    name: 'User',
    description: 'Standard user access for time tracking',
    userCount: 145,
    isSystem: true,
    isActive: true,
    permissions: ['dashboard.view_own', 'timesheet.view_own', 'timesheet.create', 'timesheet.edit_own', 'timesheet.delete_own', 'timesheet.submit', 'projects.view_assigned'],
  },
  '3': {
    id: '3',
    name: 'Approver',
    description: 'Can approve timesheets and manage team data',
    userCount: 15,
    isSystem: true,
    isActive: true,
    permissions: ['dashboard.view_own', 'dashboard.view_team', 'timesheet.view_own', 'timesheet.view_team', 'approvals.view_queue', 'approvals.approve', 'approvals.reject', 'reports.view_own', 'reports.view_team'],
  },
};

// Reusable permission card — label + description left, control right
const PermissionCard: React.FC<{
  label: React.ReactNode;
  description: string;
  control: React.ReactNode;
  dimmed?: boolean;
  onToggle?: () => void;
}> = ({ label, description, control, dimmed, onToggle }) => (
  <div
    className={`flex items-center justify-between gap-4 p-4 rounded-lg border bg-background${dimmed ? ' opacity-60' : ''}${onToggle ? ' cursor-pointer select-none' : ''}`}
    onClick={onToggle}
  >
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium">{label}</div>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
    <div className="shrink-0" onClick={e => e.stopPropagation()}>{control}</div>
  </div>
);

// Indented sub-card shown when a report is enabled
const SubPermissionCard: React.FC<{
  label: string;
  description: string;
  control: React.ReactNode;
  onToggle?: () => void;
}> = ({ label, description, control, onToggle }) => (
  <div
    className={`flex items-center justify-between gap-4 p-3 rounded-lg border bg-muted/30 ml-4${onToggle ? ' cursor-pointer select-none' : ''}`}
    onClick={onToggle}
  >
    <div className="flex-1 min-w-0">
      <div className="text-sm font-medium">{label}</div>
      <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
    </div>
    <div className="shrink-0" onClick={e => e.stopPropagation()}>{control}</div>
  </div>
);

// Section accordion item used across all tabs
const SectionAccordionItem: React.FC<{
  value: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ value, icon, title, description, children }) => (
  <AccordionItem value={value} className="border rounded-lg">
    <AccordionTrigger className="px-4">
      <div className="flex items-center gap-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          {icon}
        </div>
        <div className="text-left">
          <div className="font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground font-normal">{description}</div>
        </div>
      </div>
    </AccordionTrigger>
    <AccordionContent>
      <div className="px-4 pb-4 space-y-3">
        {children}
      </div>
    </AccordionContent>
  </AccordionItem>
);

export const EditRolePage: React.FC<EditRolePageProps> = ({ onNavigate }) => {
  const { roleId } = useParams<{ roleId: string }>();
  const navigate = useNavigate();
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [enabledPermissions, setEnabledPermissions] = useState<Set<string>>(new Set());
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSystem, setIsSystem] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [activeTab, setActiveTab] = useState<string>('details');
  const [users, setUsers] = useState<UserData[]>([]);
  const [transferDialogOpen, setTransferDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [newRoleId, setNewRoleId] = useState<string>('');
  const [nameError, setNameError] = useState<string>('');
  const [allRoles, setAllRoles] = useState<UserRoleListItem[]>([]);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [permissionChanges, setPermissionChanges] = useState<{ added: string[]; removed: string[] }>({ added: [], removed: [] });

  // Timesheet Access Settings
  const [viewTimesheetsAccess, setViewTimesheetsAccess] = useState<string>('own');
  const [editTimesheetsAccess, setEditTimesheetsAccess] = useState<string>('own');
  const [allowDeleteTimesheets, setAllowDeleteTimesheets] = useState(false);
  const [allowDuplicateTimesheets, setAllowDuplicateTimesheets] = useState(false);
  const [allowRecallTimesheet, setAllowRecallTimesheet] = useState(false);

  // Smart Features Permissions
  const [enableCopyFromLastWeek, setEnableCopyFromLastWeek] = useState(false);
  const [enableCopyDescription, setEnableCopyDescription] = useState(false);
  const [enableCopyOnlyBillable, setEnableCopyOnlyBillable] = useState(false);
  const [enableAutoSmartFill, setEnableAutoSmartFill] = useState(false);
  const [enableAiInsights, setEnableAiInsights] = useState(false);

  // Approval permissions
  const [viewApprovalDashboard, setViewApprovalDashboard] = useState(false);
  const [viewApprovals, setViewApprovals] = useState(false);
  const [editTimesheetsApproval, setEditTimesheetsApproval] = useState(false);
  const [rejectTimesheets, setRejectTimesheets] = useState(false);
  const [compulsoryRejectionReason, setCompulsoryRejectionReason] = useState(false);
  const [bulkApprove, setBulkApprove] = useState(false);
  const [delegateApproval, setDelegateApproval] = useState(false);
  const [viewPolicyViolations, setViewPolicyViolations] = useState(false);
  const [viewDetectedIssues, setViewDetectedIssues] = useState(false);

  // Report Access permissions
  const [enableTimesheetReport, setEnableTimesheetReport] = useState(false);
  const [viewTimesheetReportAccess, setViewTimesheetReportAccess] = useState<string>('own');
  const [allowTimesheetReportExport, setAllowTimesheetReportExport] = useState(false);
  const [enableMissingTimesheetReport, setEnableMissingTimesheetReport] = useState(false);
  const [viewMissingTimesheetReportAccess, setViewMissingTimesheetReportAccess] = useState<string>('own');
  const [allowMissingTimesheetReportExport, setAllowMissingTimesheetReportExport] = useState(false);
  const [enableUtilisationReport, setEnableUtilisationReport] = useState(false);
  const [viewUtilisationReportAccess, setViewUtilisationReportAccess] = useState<string>('own');
  const [allowUtilisationReportExport, setAllowUtilisationReportExport] = useState(false);
  
  // Billable Hours Report removed — belongs in the Projects module of the PSA suite, not the Timesheet module.

  // Management permissions
  const [viewEmployeeDirectory, setViewEmployeeDirectory] = useState(false);
  const [viewEmployeeDirectoryAccess, setViewEmployeeDirectoryAccess] = useState<string>('own');
  const [viewEmployeeDetails, setViewEmployeeDetails] = useState(false);

  // Data & Integration permissions
  const [viewIntegrations, setViewIntegrations] = useState(false);
  const [manageIntegrations, setManageIntegrations] = useState(false);
  const [viewCompanies, setViewCompanies] = useState(false);
  const [manageCompanies, setManageCompanies] = useState(false);

  // Administration permissions
  const [manageGeneralSettings, setManageGeneralSettings] = useState<'view' | 'configure'>('view');

  const [manageDeadlines, setManageDeadlines] = useState(false);
  const [managePolicySettings, setManagePolicySettings] = useState(false);
  const [manageUtilisationSettings, setManageUtilisationSettings] = useState(false);
  const [manageTimeEntrySettings, setManageTimeEntrySettings] = useState(false);
  const [manageWorkTypeSettings, setManageWorkTypeSettings] = useState(false);
  const [manageApprovalsSettings, setManageApprovalsSettings] = useState(false);
  const [manageNotifications, setManageNotifications] = useState(false);
  const [manageRolesPermissions, setManageRolesPermissions] = useState(false);
  // manageScreenDataModes — hidden from UI pending Settings menu visibility decision (see docs/features/roles-and-permissions.md)
  const [manageScreenDataModes, setManageScreenDataModes] = useState(false);

  const globalSettings = useGlobalSettings();
  const { getScreenMode } = useDataSource();
  const { canManageRolesPermissions } = usePermissions();
  const screenMode = getScreenMode('roles-permissions');
  const userRoleService = useMemo(() => getUserRoleService(screenMode), [screenMode]);
  const isCreateMode = !roleId || roleId === 'create';
  const [accessDenied, setAccessDenied] = useState(false);

  // ── Unsaved changes navigation guard ─────────────────────────────────────
  const { setUnsavedChanges, confirmNavigation: confirmNav } = useUnsavedChanges();

  useEffect(() => {
    setUnsavedChanges(hasUnsavedChanges);
  }, [hasUnsavedChanges, setUnsavedChanges]);

  useEffect(() => {
    return () => setUnsavedChanges(false);
  }, [setUnsavedChanges]);

  // ── Permission snapshot for diff computation ────────────────────────────
  type PermissionSnapshot = Record<string, boolean | string>;

  const PERMISSION_LABELS: Record<string, string> = {
    viewTimesheetsAccess: 'View Timesheets Access',
    editTimesheetsAccess: 'Edit Timesheets Access',
    allowDeleteTimesheets: 'Delete Timesheets',
    allowDuplicateTimesheets: 'Duplicate Timesheets',
    allowRecallTimesheet: 'Recall Timesheet',
    enableCopyFromLastWeek: 'Copy From Last Week',
    enableCopyDescription: 'Copy Description',
    enableCopyOnlyBillable: 'Copy Only Billable',
    enableAutoSmartFill: 'Auto Smart Fill',
    enableAiInsights: 'AI Insights',
    viewApprovalDashboard: 'View Approval Dashboard',
    viewApprovals: 'View Approvals',
    editTimesheetsApproval: 'Edit Timesheets (Approval)',
    rejectTimesheets: 'Reject Timesheets',
    compulsoryRejectionReason: 'Compulsory Rejection Reason',
    bulkApprove: 'Bulk Approve & Reject',
    delegateApproval: 'Delegate Approval',
    viewPolicyViolations: 'View Policy Violations',
    viewDetectedIssues: 'View Detected Issues',
    enableTimesheetReport: 'Timesheet Report',
    allowTimesheetReportExport: 'Timesheet Report Export',
    enableMissingTimesheetReport: 'Missing Timesheet Report',
    allowMissingTimesheetReportExport: 'Missing Timesheet Report Export',
    enableUtilisationReport: 'Utilisation Report',
    allowUtilisationReportExport: 'Utilisation Report Export',
    viewEmployeeDirectory: 'View Employee Directory',
    viewEmployeeDetails: 'View Employee Details',
    viewIntegrations: 'View Integrations',
    manageIntegrations: 'Manage Integrations',
    viewCompanies: 'View Companies',
    manageCompanies: 'Manage Companies',
    manageGeneralSettings: 'Manage General Settings',
    manageDeadlines: 'Manage Deadlines',
    managePolicySettings: 'Manage Policy Settings',
    manageUtilisationSettings: 'Manage Utilisation Settings',
    manageTimeEntrySettings: 'Manage Time Entry Settings',
    manageWorkTypeSettings: 'Manage Work Type Settings',
    manageApprovalsSettings: 'Manage Approvals Settings',
    manageNotifications: 'Manage Notifications',
    manageRolesPermissions: 'Manage Roles & Permissions',
    manageScreenDataModes: 'Manage Screen Data Modes',
  };

  const initialSnapshotRef = useRef<PermissionSnapshot | null>(null);

  const getCurrentSnapshot = useCallback((): PermissionSnapshot => {
    const snap: PermissionSnapshot = {};
    enabledPermissions.forEach(p => { snap[`perm:${p}`] = true; });
    snap.viewTimesheetsAccess = viewTimesheetsAccess;
    snap.editTimesheetsAccess = editTimesheetsAccess;
    snap.allowDeleteTimesheets = allowDeleteTimesheets;
    snap.allowDuplicateTimesheets = allowDuplicateTimesheets;
    snap.allowRecallTimesheet = allowRecallTimesheet;
    snap.enableCopyFromLastWeek = enableCopyFromLastWeek;
    snap.enableCopyDescription = enableCopyDescription;
    snap.enableCopyOnlyBillable = enableCopyOnlyBillable;
    snap.enableAutoSmartFill = enableAutoSmartFill;
    snap.enableAiInsights = enableAiInsights;
    snap.viewApprovalDashboard = viewApprovalDashboard;
    snap.viewApprovals = viewApprovals;
    snap.editTimesheetsApproval = editTimesheetsApproval;
    snap.rejectTimesheets = rejectTimesheets;
    snap.compulsoryRejectionReason = compulsoryRejectionReason;
    snap.bulkApprove = bulkApprove;
    snap.delegateApproval = delegateApproval;
    snap.viewPolicyViolations = viewPolicyViolations;
    snap.viewDetectedIssues = viewDetectedIssues;
    snap.enableTimesheetReport = enableTimesheetReport;
    snap.allowTimesheetReportExport = allowTimesheetReportExport;
    snap.enableMissingTimesheetReport = enableMissingTimesheetReport;
    snap.allowMissingTimesheetReportExport = allowMissingTimesheetReportExport;
    snap.enableUtilisationReport = enableUtilisationReport;
    snap.allowUtilisationReportExport = allowUtilisationReportExport;
    snap.viewEmployeeDirectory = viewEmployeeDirectory;
    snap.viewEmployeeDirectoryAccess = viewEmployeeDirectoryAccess;
    snap.viewEmployeeDetails = viewEmployeeDetails;
    snap.viewIntegrations = viewIntegrations;
    snap.manageIntegrations = manageIntegrations;
    snap.viewCompanies = viewCompanies;
    snap.manageCompanies = manageCompanies;
    snap.manageGeneralSettings = manageGeneralSettings;
    snap.manageDeadlines = manageDeadlines;
    snap.managePolicySettings = managePolicySettings;
    snap.manageUtilisationSettings = manageUtilisationSettings;
    snap.manageTimeEntrySettings = manageTimeEntrySettings;
    snap.manageWorkTypeSettings = manageWorkTypeSettings;
    snap.manageApprovalsSettings = manageApprovalsSettings;
    snap.manageNotifications = manageNotifications;
    snap.manageRolesPermissions = manageRolesPermissions;
    snap.manageScreenDataModes = manageScreenDataModes;
    return snap;
  }, [enabledPermissions, viewTimesheetsAccess, editTimesheetsAccess, allowDeleteTimesheets, allowDuplicateTimesheets, allowRecallTimesheet, enableCopyFromLastWeek, enableCopyDescription, enableCopyOnlyBillable, enableAutoSmartFill, enableAiInsights, viewApprovalDashboard, viewApprovals, editTimesheetsApproval, rejectTimesheets, compulsoryRejectionReason, bulkApprove, delegateApproval, viewPolicyViolations, viewDetectedIssues, enableTimesheetReport, allowTimesheetReportExport, enableMissingTimesheetReport, allowMissingTimesheetReportExport, enableUtilisationReport, allowUtilisationReportExport, viewEmployeeDirectory, viewEmployeeDetails, viewIntegrations, manageIntegrations, viewCompanies, manageCompanies, manageGeneralSettings, manageDeadlines, managePolicySettings, manageUtilisationSettings, manageTimeEntrySettings, manageWorkTypeSettings, manageApprovalsSettings, manageNotifications, manageRolesPermissions, manageScreenDataModes]);

  const getPermissionLabel = useCallback((key: string): string => {
    if (key.startsWith('perm:')) {
      const permId = key.slice(5);
      for (const cat of permissionCategories) {
        const allPerms = cat.groups ? cat.groups.flatMap(g => g.permissions) : cat.permissions;
        const found = allPerms.find(p => p.id === permId);
        if (found) return found.name;
      }
      return permId;
    }
    return PERMISSION_LABELS[key] || key;
  }, []);

  const computePermissionDiff = useCallback((): { added: string[]; removed: string[] } => {
    const initial = initialSnapshotRef.current;
    if (!initial) return { added: [], removed: [] };
    const current = getCurrentSnapshot();
    const added: string[] = [];
    const removed: string[] = [];
    const allKeys = new Set([...Object.keys(initial), ...Object.keys(current)]);
    for (const key of allKeys) {
      const initialVal = initial[key];
      const currentVal = current[key];
      if (key.startsWith('perm:')) {
        if (!initialVal && currentVal) added.push(getPermissionLabel(key));
        else if (initialVal && !currentVal) removed.push(getPermissionLabel(key));
      } else if (key === 'viewTimesheetsAccess' || key === 'editTimesheetsAccess' || key === 'manageGeneralSettings') {
        const wasEnabled = initialVal !== 'none' && initialVal !== 'view';
        const isEnabled = currentVal !== 'none' && currentVal !== 'view';
        if (!wasEnabled && isEnabled) added.push(getPermissionLabel(key));
        else if (wasEnabled && !isEnabled) removed.push(getPermissionLabel(key));
      } else {
        if (!initialVal && currentVal) added.push(getPermissionLabel(key));
        else if (initialVal && !currentVal) removed.push(getPermissionLabel(key));
      }
    }
    return { added, removed };
  }, [getCurrentSnapshot, getPermissionLabel]);

  // ── Helpers ──────────────────────────────────────────────────────────────

  const isCategoryFullyEnabled = useCallback((category: PermissionCategory): boolean => {
    const allPerms = category.groups
      ? category.groups.flatMap(g => g.permissions)
      : category.permissions;
    return allPerms.length > 0 && allPerms.every(p => enabledPermissions.has(p.id));
  }, [enabledPermissions]);

  const getCategoryEnabledCount = useCallback((category: PermissionCategory): number => {
    const allPerms = category.groups
      ? category.groups.flatMap(g => g.permissions)
      : category.permissions;
    return allPerms.filter(p => enabledPermissions.has(p.id)).length;
  }, [enabledPermissions]);

  const getCategorySummaryCount = useCallback((categoryId: string) => {
    switch (categoryId) {
      case 'dashboard': {
        const category = permissionCategories.find(c => c.id === 'dashboard');
        if (!category) return { enabled: 0, total: 0 };
        const allPerms = category.permissions;
        return { enabled: allPerms.filter(p => enabledPermissions.has(p.id)).length, total: allPerms.length };
      }
      case 'timesheet': {
        let enabled = 0;
        if (viewTimesheetsAccess !== 'none') enabled++;
        if (editTimesheetsAccess !== 'none') enabled++;
        if (allowDeleteTimesheets) enabled++;
        if (allowDuplicateTimesheets) enabled++;
        if (allowRecallTimesheet) enabled++;
        if (enableCopyFromLastWeek) enabled++;
        if (enableCopyDescription) enabled++;
        if (enableCopyOnlyBillable) enabled++;
        if (enableAutoSmartFill) enabled++;
        if (enableAiInsights) enabled++;
        return { enabled, total: 10 };
      }
      case 'approvals': {
        let enabled = 0;
        if (viewApprovalDashboard) enabled++;
        if (viewApprovals) enabled++;
        if (editTimesheetsApproval) enabled++;
        if (rejectTimesheets) enabled++;
        if (compulsoryRejectionReason) enabled++;
        if (bulkApprove) enabled++;
        if (delegateApproval) enabled++;
        if (viewPolicyViolations) enabled++;
        if (viewDetectedIssues) enabled++;
        return { enabled, total: 9 };
      }
      case 'reports': {
        let enabled = 0;
        if (enableTimesheetReport) enabled++;
        if (allowTimesheetReportExport) enabled++;
        if (enableMissingTimesheetReport) enabled++;
        if (allowMissingTimesheetReportExport) enabled++;
        if (enableUtilisationReport) enabled++;
        if (allowUtilisationReportExport) enabled++;
        return { enabled, total: 6 };
      }
      case 'team': {
        let enabled = 0;
        if (viewEmployeeDirectory) enabled++;
        if (viewEmployeeDetails) enabled++;
        return { enabled, total: 2 };
      }
      case 'projects': {
        const category = permissionCategories.find(c => c.id === 'projects');
        if (!category) return { enabled: 0, total: 0 };
        const allPerms = category.permissions;
        return { enabled: allPerms.filter(p => enabledPermissions.has(p.id)).length, total: allPerms.length };
      }
      case 'integrations': {
        let enabled = 0;
        if (viewIntegrations) enabled++;
        if (manageIntegrations) enabled++;
        if (viewCompanies) enabled++;
        if (manageCompanies) enabled++;
        return { enabled, total: 4 };
      }
      case 'settings': {
        let enabled = 0;
        if (manageGeneralSettings === 'configure') enabled++;
        if (manageDeadlines) enabled++;
        if (managePolicySettings) enabled++;
        if (manageUtilisationSettings) enabled++;
        if (manageTimeEntrySettings) enabled++;
        if (manageWorkTypeSettings) enabled++;
        if (manageApprovalsSettings) enabled++;
        if (manageNotifications) enabled++;
        if (manageRolesPermissions) enabled++;
        return { enabled, total: 9 };
      }
      case 'data': {
        const category = permissionCategories.find(c => c.id === 'data');
        if (!category) return { enabled: 0, total: 0 };
        const allPerms = category.permissions;
        return { enabled: allPerms.filter(p => enabledPermissions.has(p.id)).length, total: allPerms.length };
      }
      default:
        return { enabled: 0, total: 0 };
    }
  }, [enabledPermissions, viewTimesheetsAccess, editTimesheetsAccess, allowDeleteTimesheets, allowDuplicateTimesheets, allowRecallTimesheet, enableCopyFromLastWeek, enableCopyDescription, enableCopyOnlyBillable, enableAutoSmartFill, enableAiInsights, viewApprovalDashboard, viewApprovals, editTimesheetsApproval, rejectTimesheets, compulsoryRejectionReason, bulkApprove, delegateApproval, viewPolicyViolations, viewDetectedIssues, enableTimesheetReport, allowTimesheetReportExport, enableMissingTimesheetReport, allowMissingTimesheetReportExport, enableUtilisationReport, allowUtilisationReportExport, viewEmployeeDirectory, viewEmployeeDetails, viewIntegrations, manageIntegrations, viewCompanies, manageCompanies, manageGeneralSettings, manageDeadlines, managePolicySettings, manageUtilisationSettings, manageTimeEntrySettings, manageWorkTypeSettings, manageApprovalsSettings, manageNotifications, manageRolesPermissions]);

  const getTotalPermissionsCount = useCallback((): number => {
    let count = enabledPermissions.size;
    if (viewTimesheetsAccess !== 'none') count++;
    if (editTimesheetsAccess !== 'none') count++;
    if (allowDeleteTimesheets) count++;
    if (allowDuplicateTimesheets) count++;
    if (allowRecallTimesheet) count++;
    if (enableCopyFromLastWeek) count++;
    if (enableCopyDescription) count++;
    if (enableCopyOnlyBillable) count++;
    if (enableAutoSmartFill) count++;
    if (enableAiInsights) count++;
    if (viewApprovalDashboard) count++;
    if (viewApprovals) count++;
    if (editTimesheetsApproval) count++;
    if (rejectTimesheets) count++;
    if (compulsoryRejectionReason) count++;
    if (bulkApprove) count++;
    if (delegateApproval) count++;
    if (viewPolicyViolations) count++;
    if (viewDetectedIssues) count++;
    if (enableTimesheetReport) count++;
    if (allowTimesheetReportExport) count++;
    if (enableMissingTimesheetReport) count++;
    if (allowMissingTimesheetReportExport) count++;
    if (enableUtilisationReport) count++;
    if (allowUtilisationReportExport) count++;

    if (viewEmployeeDirectory) count++;
    if (viewEmployeeDetails) count++;
    if (viewIntegrations) count++;
    if (manageIntegrations) count++;
    if (viewCompanies) count++;
    if (manageCompanies) count++;
    if (manageGeneralSettings === 'configure') count++;

    if (manageDeadlines) count++;
    if (managePolicySettings) count++;
    if (manageUtilisationSettings) count++;
    if (manageTimeEntrySettings) count++;
    if (manageWorkTypeSettings) count++;
    if (manageApprovalsSettings) count++;
    if (manageNotifications) count++;
    if (manageRolesPermissions) count++;
    // manageScreenDataModes excluded from count — hidden from UI
    
    return count;
  }, [enabledPermissions, viewTimesheetsAccess, editTimesheetsAccess, allowDeleteTimesheets, allowDuplicateTimesheets, allowRecallTimesheet, enableCopyFromLastWeek, enableCopyDescription, enableCopyOnlyBillable, enableAutoSmartFill, enableAiInsights, viewApprovalDashboard, viewApprovals, editTimesheetsApproval, rejectTimesheets, compulsoryRejectionReason, bulkApprove, delegateApproval, viewPolicyViolations, viewDetectedIssues, enableTimesheetReport, allowTimesheetReportExport, enableMissingTimesheetReport, allowMissingTimesheetReportExport, enableUtilisationReport, allowUtilisationReportExport,   viewEmployeeDirectory, viewEmployeeDetails, viewIntegrations, manageIntegrations, viewCompanies, manageCompanies, manageGeneralSettings, manageDeadlines, managePolicySettings, manageUtilisationSettings, manageTimeEntrySettings, manageWorkTypeSettings, manageApprovalsSettings, manageNotifications, manageRolesPermissions, manageScreenDataModes]);

    const getTabPermissionCount = useCallback((tab: string): number => {
    switch (tab) {
      case 'time-tracking': {
        let c = 0;
        if (enabledPermissions.has('dashboard.view_own')) c++;
        if (enabledPermissions.has('dashboard.view_team')) c++;
        if (enabledPermissions.has('dashboard.view_department')) c++;
        if (viewTimesheetsAccess !== 'none') c++;
        if (editTimesheetsAccess !== 'none') c++;
        if (allowDeleteTimesheets) c++;
        if (allowDuplicateTimesheets) c++;
        if (allowRecallTimesheet) c++;
        if (enableCopyFromLastWeek) c++;
        if (enableCopyDescription) c++;
        if (enableCopyOnlyBillable) c++;
        if (enableAutoSmartFill) c++;
        if (enableAiInsights) c++;
        return c;
      }
      case 'approval': {
        let c = 0;
        if (viewApprovalDashboard) c++;
        if (viewApprovals) c++;
        if (editTimesheetsApproval) c++;
        if (rejectTimesheets) c++;
        if (compulsoryRejectionReason) c++;
        if (bulkApprove) c++;
        if (delegateApproval) c++;
        if (viewPolicyViolations) c++;
        if (viewDetectedIssues) c++;
        return c;
      }
      case 'reports': {
        let c = 0;
        if (enableTimesheetReport) c++;
        if (allowTimesheetReportExport) c++;
        if (enableMissingTimesheetReport) c++;
        if (allowMissingTimesheetReportExport) c++;
        if (enableUtilisationReport) c++;
        if (allowUtilisationReportExport) c++;
        return c;
      }
      case 'management': {
        let c = 0;
        if (viewEmployeeDirectory) c++;
        if (viewEmployeeDetails) c++;
        return c;
      }
      case 'data': {
        let c = 0;
        if (viewIntegrations) c++;
        if (manageIntegrations) c++;
        if (viewCompanies) c++;
        if (manageCompanies) c++;
        return c;
      }
      case 'administration': {
        let c = 0;
        if (manageGeneralSettings === 'configure') c++;
        if (manageDeadlines) c++;
        if (managePolicySettings) c++;
        if (manageUtilisationSettings) c++;
        if (manageTimeEntrySettings) c++;
        if (manageWorkTypeSettings) c++;
        if (manageApprovalsSettings) c++;
        if (manageNotifications) c++;
        if (manageRolesPermissions) c++;
        if (manageScreenDataModes) c++;
        return c;
      }
      default:
        return 0;
    }
  }, [enabledPermissions, viewTimesheetsAccess, editTimesheetsAccess, allowDeleteTimesheets, allowDuplicateTimesheets, allowRecallTimesheet, enableCopyFromLastWeek, enableCopyDescription, enableCopyOnlyBillable, enableAutoSmartFill, enableAiInsights, viewApprovalDashboard, viewApprovals, editTimesheetsApproval, rejectTimesheets, compulsoryRejectionReason, bulkApprove, delegateApproval, viewPolicyViolations, viewDetectedIssues, enableTimesheetReport, allowTimesheetReportExport, enableMissingTimesheetReport, allowMissingTimesheetReportExport, enableUtilisationReport, allowUtilisationReportExport,   viewEmployeeDirectory, viewEmployeeDetails, viewIntegrations, manageIntegrations, viewCompanies, manageCompanies, manageGeneralSettings, manageDeadlines, managePolicySettings, manageUtilisationSettings, manageTimeEntrySettings, manageWorkTypeSettings, manageApprovalsSettings, manageNotifications, manageRolesPermissions, manageScreenDataModes]);

    const handleTogglePermission = (permissionId: string) => {
    setEnabledPermissions(prev => {
      const newSet = new Set(prev);
      if (newSet.has(permissionId)) newSet.delete(permissionId);
      else newSet.add(permissionId);
      return newSet;
    });
    setHasUnsavedChanges(true);
  };

  const handleToggleCategory = (category: PermissionCategory, enabled: boolean) => {
    setEnabledPermissions(prev => {
      const newSet = new Set(prev);
      category.permissions.forEach(permission => {
        if (enabled) newSet.add(permission.id);
        else newSet.delete(permission.id);
      });
      return newSet;
    });
    setHasUnsavedChanges(true);
  };

  // ── Bulk actions ──────────────────────────────────────────────────────────

  const handleSelectAllTimeTracking = () => {
    const newPermissions = new Set(enabledPermissions);
    newPermissions.add('dashboard.view_own');
    newPermissions.add('dashboard.view_team');
    newPermissions.add('dashboard.view_department');
    setEnabledPermissions(newPermissions);
    setAllowRecallTimesheet(true);
    setAllowDuplicateTimesheets(true);
    setAllowDeleteTimesheets(true);
    setEnableCopyFromLastWeek(globalSettings.enableCopyPreviousWeek);
    setEnableCopyDescription(globalSettings.copyDescriptions);
    setEnableCopyOnlyBillable(globalSettings.copyOnlyBillable);
    setEnableAutoSmartFill(globalSettings.enableAutoSmartFill);
    setEnableAiInsights(globalSettings.enableAiInsights);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllTimeTracking = () => {
    const newPermissions = new Set(enabledPermissions);
    newPermissions.delete('dashboard.view_own');
    newPermissions.delete('dashboard.view_team');
    newPermissions.delete('dashboard.view_department');
    setEnabledPermissions(newPermissions);
    setAllowRecallTimesheet(false);
    setAllowDuplicateTimesheets(false);
    setAllowDeleteTimesheets(false);
    setEnableCopyFromLastWeek(false);
    setEnableCopyDescription(false);
    setEnableCopyOnlyBillable(false);
    setEnableAutoSmartFill(false);
    setEnableAiInsights(false);
    setHasUnsavedChanges(true);
  };

  const handleSelectAllApproval = () => {
    setViewApprovalDashboard(true); setViewApprovals(true); setEditTimesheetsApproval(true);
    setRejectTimesheets(true); setCompulsoryRejectionReason(true); setBulkApprove(true);
    setDelegateApproval(true); setViewPolicyViolations(true); setViewDetectedIssues(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllApproval = () => {
    setViewApprovalDashboard(false); setViewApprovals(false); setEditTimesheetsApproval(false);
    setRejectTimesheets(false); setCompulsoryRejectionReason(false); setBulkApprove(false);
    setDelegateApproval(false); setViewPolicyViolations(false); setViewDetectedIssues(false);
    setHasUnsavedChanges(true);
  };

  const handleSelectAllReports = () => {
    setEnableTimesheetReport(true); setAllowTimesheetReportExport(true);
    setEnableMissingTimesheetReport(true); setAllowMissingTimesheetReportExport(true);
    setEnableUtilisationReport(true); setAllowUtilisationReportExport(true);
    // billableHoursReport removed — belongs in the Projects module of the PSA suite.
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllReports = () => {
    setEnableTimesheetReport(false); setAllowTimesheetReportExport(false);
    setEnableMissingTimesheetReport(false); setAllowMissingTimesheetReportExport(false);
    setEnableUtilisationReport(false); setAllowUtilisationReportExport(false);
    // billableHoursReport removed — belongs in the Projects module of the PSA suite.
    setHasUnsavedChanges(true);
  };

  const handleSelectAllManagement = () => {
    setViewEmployeeDirectory(true); setViewEmployeeDirectoryAccess('all'); setViewEmployeeDetails(true); setHasUnsavedChanges(true);
  };

  const handleDeselectAllManagement = () => {
    setViewEmployeeDirectory(false); setViewEmployeeDirectoryAccess('own'); setViewEmployeeDetails(false); setHasUnsavedChanges(true);
  };

  const handleSelectAllDataIntegration = () => {
    setViewIntegrations(true); setManageIntegrations(true);
    setViewCompanies(true); setManageCompanies(true); setHasUnsavedChanges(true);
  };

  const handleDeselectAllDataIntegration = () => {
    setViewIntegrations(false); setManageIntegrations(false);
    setViewCompanies(false); setManageCompanies(false); setHasUnsavedChanges(true);
  };

  const handleSelectAllAdministration = () => {
    setManageGeneralSettings('configure'); setManageDeadlines(true);
    setManagePolicySettings(true); setManageUtilisationSettings(true);
    setManageTimeEntrySettings(true); setManageWorkTypeSettings(true);
    setManageApprovalsSettings(true);
    setManageNotifications(true); setManageRolesPermissions(true); setManageScreenDataModes(true);
    setHasUnsavedChanges(true);
  };

  const handleDeselectAllAdministration = () => {
    setManageGeneralSettings('view'); setManageDeadlines(false);
    setManagePolicySettings(false); setManageUtilisationSettings(false);
    setManageTimeEntrySettings(false); setManageWorkTypeSettings(false);
    setManageApprovalsSettings(false);
    setManageNotifications(false);
    // System roles must always retain manageRolesPermissions
    if (!isSystem) {
      setManageRolesPermissions(false);
    }
    setManageScreenDataModes(false);
    setHasUnsavedChanges(true);
  };

  // ── Load / save ───────────────────────────────────────────────────────────

  const buildSnapshotFromData = (permissionsData: Record<string, unknown>): PermissionSnapshot => {
    const snap: PermissionSnapshot = {};
    if (permissionsData.permissions && Array.isArray(permissionsData.permissions)) {
      (permissionsData.permissions as string[]).forEach(p => { snap[`perm:${p}`] = true; });
    }
    const ta = permissionsData.timesheetAccess as Record<string, unknown> | undefined;
    snap.viewTimesheetsAccess = (ta?.viewAccess as string) || 'own';
    snap.editTimesheetsAccess = (ta?.editAccess as string) || 'own';
    snap.allowDeleteTimesheets = !!(ta?.allowDelete);
    snap.allowDuplicateTimesheets = !!(ta?.allowDuplicate);
    snap.allowRecallTimesheet = !!(ta?.allowRecall);
    const sf = permissionsData.smartFeaturesPermissions as Record<string, unknown> | undefined;
    snap.enableCopyFromLastWeek = !!(sf?.enableCopyFromLastWeek);
    snap.enableCopyDescription = !!(sf?.enableCopyDescription);
    snap.enableCopyOnlyBillable = !!(sf?.enableCopyOnlyBillable);
    snap.enableAutoSmartFill = !!(sf?.enableAutoSmartFill);
    snap.enableAiInsights = !!(sf?.enableAiInsights);
    const ap = permissionsData.approvalPermissions as Record<string, unknown> | undefined;
    snap.viewApprovalDashboard = !!(ap?.viewDashboard);
    const tap = permissionsData.timesheetApprovalPermissions as Record<string, unknown> | undefined;
    snap.viewApprovals = !!(tap?.viewApprovals);
    snap.editTimesheetsApproval = !!(tap?.editTimesheets);
    snap.rejectTimesheets = !!(tap?.rejectTimesheets);
    snap.compulsoryRejectionReason = !!(tap?.compulsoryRejectionReason);
    snap.bulkApprove = !!(tap?.bulkApprove);
    snap.delegateApproval = !!(tap?.delegateApproval);
    snap.viewPolicyViolations = !!(tap?.viewPolicyViolations);
    snap.viewDetectedIssues = !!(tap?.viewDetectedIssues);
    const ra = permissionsData.reportAccessPermissions as Record<string, unknown> | undefined;
    const tr = ra?.timesheetReport as Record<string, unknown> | undefined;
    snap.enableTimesheetReport = !!(tr?.enabled);
    snap.allowTimesheetReportExport = !!(tr?.allowExport);
    const mtr = ra?.missingTimesheetReport as Record<string, unknown> | undefined;
    snap.enableMissingTimesheetReport = !!(mtr?.enabled);
    snap.allowMissingTimesheetReportExport = !!(mtr?.allowExport);
    const ur = ra?.utilisationReport as Record<string, unknown> | undefined;
    snap.enableUtilisationReport = !!(ur?.enabled);
    snap.allowUtilisationReportExport = !!(ur?.allowExport);
    const ed = permissionsData.employeeDirectoryPermissions as Record<string, unknown> | undefined;
    snap.viewEmployeeDirectory = !!(ed?.viewDirectory);
    snap.viewEmployeeDirectoryAccess = (ed?.viewAccess as string) || 'own';
    snap.viewEmployeeDetails = !!(ed?.viewDetails);
    const ip = permissionsData.integrationsPermissions as Record<string, unknown> | undefined;
    snap.viewIntegrations = !!(ip?.viewIntegrations);
    snap.manageIntegrations = !!(ip?.manageIntegrations);
    const cm = permissionsData.companyManagementPermissions as Record<string, unknown> | undefined;
    snap.viewCompanies = !!(cm?.viewCompanies);
    snap.manageCompanies = !!(cm?.manageCompanies);
    const ss = permissionsData.systemSettingsPermissions as Record<string, unknown> | undefined;
    snap.manageGeneralSettings = (ss?.manageGeneralSettings as string) || 'view';
    snap.manageDeadlines = !!(ss?.manageDeadlines);
    snap.managePolicySettings = !!(ss?.managePolicySettings);
    snap.manageUtilisationSettings = !!(ss?.manageUtilisationSettings);
    snap.manageTimeEntrySettings = !!(ss?.manageTimeEntrySettings);
    snap.manageWorkTypeSettings = !!(ss?.manageWorkTypeSettings);
    snap.manageApprovalsSettings = !!(ss?.manageApprovalsSettings);
    snap.manageNotifications = !!(ss?.manageNotifications);
    snap.manageRolesPermissions = !!(ss?.manageRolesPermissions);
    snap.manageScreenDataModes = !!(ss?.manageScreenDataModes);
    return snap;
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const loadPermissionsFromData = (permissionsData: any, saveSnapshot = false) => {
    try {
      if (saveSnapshot) {
        initialSnapshotRef.current = buildSnapshotFromData(permissionsData as Record<string, unknown>);
      }
      if (permissionsData.permissions && Array.isArray(permissionsData.permissions)) {
        setEnabledPermissions(new Set(permissionsData.permissions));
      }
      if (permissionsData.timesheetAccess) {
        setViewTimesheetsAccess(permissionsData.timesheetAccess.viewAccess || 'own');
        setEditTimesheetsAccess(permissionsData.timesheetAccess.editAccess || 'own');
        setAllowDeleteTimesheets(permissionsData.timesheetAccess.allowDelete || false);
        setAllowDuplicateTimesheets(permissionsData.timesheetAccess.allowDuplicate || false);
        setAllowRecallTimesheet(permissionsData.timesheetAccess.allowRecall || false);
      }
      if (permissionsData.smartFeaturesPermissions) {
        setEnableCopyFromLastWeek(permissionsData.smartFeaturesPermissions.enableCopyFromLastWeek || false);
        setEnableCopyDescription(permissionsData.smartFeaturesPermissions.enableCopyDescription || false);
        setEnableCopyOnlyBillable(permissionsData.smartFeaturesPermissions.enableCopyOnlyBillable || false);
        setEnableAutoSmartFill(permissionsData.smartFeaturesPermissions.enableAutoSmartFill || false);
        setEnableAiInsights(permissionsData.smartFeaturesPermissions.enableAiInsights || false);
      }
      setViewApprovalDashboard(permissionsData.approvalPermissions?.viewDashboard || false);
      if (permissionsData.timesheetApprovalPermissions) {
        setViewApprovals(permissionsData.timesheetApprovalPermissions.viewApprovals || false);
        setEditTimesheetsApproval(permissionsData.timesheetApprovalPermissions.editTimesheets || false);
        setRejectTimesheets(permissionsData.timesheetApprovalPermissions.rejectTimesheets || false);
        setCompulsoryRejectionReason(permissionsData.timesheetApprovalPermissions.compulsoryRejectionReason || false);
        setBulkApprove(permissionsData.timesheetApprovalPermissions.bulkApprove || false);
        setDelegateApproval(permissionsData.timesheetApprovalPermissions.delegateApproval || false);
        setViewPolicyViolations(permissionsData.timesheetApprovalPermissions.viewPolicyViolations || false);
        setViewDetectedIssues(permissionsData.timesheetApprovalPermissions.viewDetectedIssues || false);
      }
      if (permissionsData.reportAccessPermissions) {
        setEnableTimesheetReport(permissionsData.reportAccessPermissions.timesheetReport?.enabled || false);
        setViewTimesheetReportAccess(permissionsData.reportAccessPermissions.timesheetReport?.viewAccess || 'own');
        setAllowTimesheetReportExport(permissionsData.reportAccessPermissions.timesheetReport?.allowExport || false);
        setEnableMissingTimesheetReport(permissionsData.reportAccessPermissions.missingTimesheetReport?.enabled || false);
        setViewMissingTimesheetReportAccess(permissionsData.reportAccessPermissions.missingTimesheetReport?.viewAccess || 'own');
        setAllowMissingTimesheetReportExport(permissionsData.reportAccessPermissions.missingTimesheetReport?.allowExport || false);
        setEnableUtilisationReport(permissionsData.reportAccessPermissions.utilisationReport?.enabled || false);
        setViewUtilisationReportAccess(permissionsData.reportAccessPermissions.utilisationReport?.viewAccess || 'own');
        setAllowUtilisationReportExport(permissionsData.reportAccessPermissions.utilisationReport?.allowExport || false);

        // billableHoursReport removed — belongs in the Projects module of the PSA suite.
      }

      // Reports & Analytics - Analytics & Insights section removed from Timesheet module

      // Management - Employee Directory
      if (permissionsData.employeeDirectoryPermissions) {
        setViewEmployeeDirectory(permissionsData.employeeDirectoryPermissions.viewDirectory || false);
        setViewEmployeeDirectoryAccess(permissionsData.employeeDirectoryPermissions.viewAccess || 'own');
        setViewEmployeeDetails(permissionsData.employeeDirectoryPermissions.viewDetails || false);
      }
      if (permissionsData.integrationsPermissions) {
        setViewIntegrations(permissionsData.integrationsPermissions.viewIntegrations || false);
        setManageIntegrations(permissionsData.integrationsPermissions.manageIntegrations || false);
      }
      if (permissionsData.companyManagementPermissions) {
        setViewCompanies(permissionsData.companyManagementPermissions.viewCompanies || false);
        setManageCompanies(permissionsData.companyManagementPermissions.manageCompanies || false);
      }
      if (permissionsData.systemSettingsPermissions) {
        setManageGeneralSettings(permissionsData.systemSettingsPermissions.manageGeneralSettings || 'view');

        setManageDeadlines(permissionsData.systemSettingsPermissions.manageDeadlines || false);
        setManagePolicySettings(permissionsData.systemSettingsPermissions.managePolicySettings || false);
        setManageUtilisationSettings(permissionsData.systemSettingsPermissions.manageUtilisationSettings || false);
        setManageTimeEntrySettings(permissionsData.systemSettingsPermissions.manageTimeEntrySettings || false);
        setManageWorkTypeSettings(permissionsData.systemSettingsPermissions.manageWorkTypeSettings || false);
        setManageApprovalsSettings(permissionsData.systemSettingsPermissions.manageApprovalsSettings || false);
        setManageNotifications(permissionsData.systemSettingsPermissions.manageNotifications || false);
        setManageRolesPermissions(permissionsData.systemSettingsPermissions.manageRolesPermissions || false);
        setManageScreenDataModes(permissionsData.systemSettingsPermissions.manageScreenDataModes || false);
      }
    } catch (error) {
      console.error('Error loading permissions from data:', error);
    }
  };

  const handleCancel = () => {
    const go = () => {
      if (onNavigate) onNavigate('/settings/roles-permissions');
      else navigate('/settings/roles-permissions');
    };
    confirmNav(go);
  };

  useEffect(() => {
    const loadRoleData = async () => {
      try {
        const roles = await userRoleService.getAllUserRoles();
        setAllRoles(roles);
      } catch (error) {
        console.error('Error loading all roles:', error);
        if (error instanceof ApiError && (error.status === 403 || error.status === 401)) {
          setAccessDenied(true);
          return;
        }
      }

      if (!isCreateMode && roleId) {
        localStorage.setItem(prefixKey('currentEditingRoleId'), roleId);
        window.dispatchEvent(new Event('permissionsUpdated'));
      }

      if (!isCreateMode && roleId) {
        try {
          const role = await userRoleService.getUserRoleById(Number(roleId));
          setRoleName(role.name);
          setRoleDescription(role.description || '');
          setIsSystem(role.isSystem);
          setIsActive(role.isSystem ? true : role.isActive);
          if (role.permissions) loadPermissionsFromData(role.permissions, true);
          try {
            const employees = await userRoleService.getEmployeesByRoleId(Number(roleId));
            setUsers(employees.map(emp => ({
              id: String(emp.id),
              name: emp.name,
              email: emp.email,
              roleId: roleId,
              roleName: emp.roleName || roleName,
              status: emp.status === 'active' ? 'active' : 'inactive',
            })));
          } catch {
            setUsers(mockUsers.filter(u => u.roleId === roleId));
          }
        } catch (error) {
          console.error('Error loading role:', error);
          if (error instanceof ApiError && (error.status === 403 || error.status === 401)) {
            setAccessDenied(true);
            return;
          }
        }
      } else if (isCreateMode) {
        const duplicationDataStr = sessionStorage.getItem('roleDuplicationData');
        if (duplicationDataStr) {
          try {
            const duplicationData = JSON.parse(duplicationDataStr);
            const sourceRole = await userRoleService.getUserRoleById(duplicationData.sourceRoleId);
            if (sourceRole.permissions) loadPermissionsFromData(sourceRole.permissions);
            setRoleName(`${duplicationData.sourceRoleName} (Copy)`);
            setRoleDescription('');
            sessionStorage.removeItem('roleDuplicationData');
          } catch (error) {
            console.error('Error loading duplication data:', error);
          }
        } else {
          setRoleName('');
          setRoleDescription('');
          setEnabledPermissions(new Set());
          setIsSystem(false);
          setIsActive(true);
          setUsers([]);
        }
      }
    };
    loadRoleData();
  }, [roleId, isCreateMode, userRoleService]);

  useEffect(() => {
    return () => {
      localStorage.removeItem(prefixKey('currentEditingRoleId'));
      window.dispatchEvent(new Event('permissionsUpdated'));
    };
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const smartFeatureItems = [
    {
      id: 'copy-last-week',
      label: "Enable 'Copy from Last Week'",
      description: 'Allow users to copy entries from previous week',
      enabled: globalSettings.enableCopyPreviousWeek,
      value: enableCopyFromLastWeek,
      setter: setEnableCopyFromLastWeek,
    },
    {
      id: 'copy-description',
      label: 'Copy Description',
      description: 'Include entry descriptions when copying',
      enabled: globalSettings.copyDescriptions,
      value: enableCopyDescription,
      setter: setEnableCopyDescription,
    },
    {
      id: 'copy-billable',
      label: 'Copy only Billable Entries',
      description: 'When copying, exclude non-billable time',
      enabled: globalSettings.copyOnlyBillable,
      value: enableCopyOnlyBillable,
      setter: setEnableCopyOnlyBillable,
    },
    {
      id: 'auto-smart-fill',
      label: 'Enable Auto Smart Fill',
      description: 'Automatically suggest time entries based on patterns',
      enabled: globalSettings.enableAutoSmartFill,
      value: enableAutoSmartFill,
      setter: setEnableAutoSmartFill,
    },
    {
      id: 'ai-insights',
      label: 'Enable AI Insights',
      description: 'Show AI-powered insights and anomaly detection',
      enabled: globalSettings.enableAiInsights,
      value: enableAiInsights,
      setter: setEnableAiInsights,
    },
  ];

  const adminSubSettings = [
    {
      id: 'manage-deadlines',
      label: 'Manage Deadlines',
      description: 'Define and adjust timesheet submission cutoff dates, approval deadlines, and period-end schedules',
      value: manageDeadlines,
      setter: setManageDeadlines,
    },
    {
      id: 'manage-policy-settings',
      label: 'Manage Policy Settings',
      description: 'Configure automated rules for detecting and flagging policy violations, including overtime limits and break requirements',
      value: managePolicySettings,
      setter: setManagePolicySettings,
    },
    {
      id: 'manage-utilisation-settings',
      label: 'Manage Utilisation Settings',
      description: 'Set target utilization rates, billability thresholds, and capacity planning parameters for resources',
      value: manageUtilisationSettings,
      setter: setManageUtilisationSettings,
    },
    {
      id: 'manage-time-entry-settings',
      label: 'Manage Time Entry Settings',
      description: 'Configure time entry rules, validation settings, and entry submission requirements',
      value: manageTimeEntrySettings,
      setter: setManageTimeEntrySettings,
    },
    {
      id: 'manage-work-type-settings',
      label: 'Manage Work Type Settings',
      description: 'Define and manage work types, overtime classifications, and billing rate multipliers',
      value: manageWorkTypeSettings,
      setter: setManageWorkTypeSettings,
    },
    {
      id: 'manage-approvals',
      label: 'Manage Approvals',
      description: 'Design and modify approval chains, set approval hierarchies, and configure escalation rules for timesheets and requests',
      value: manageApprovalsSettings,
      setter: setManageApprovalsSettings,
    },
    {
      id: 'manage-notifications',
      label: 'Manage Notifications',
      description: 'Control when and how users receive email, in-app, and push notifications for various system events and reminders',
      value: manageNotifications,
      setter: setManageNotifications,
    },
    {
      id: 'manage-screen-data-modes',
      label: 'Manage Screen Data Modes',
      description: 'Enable or disable demonstration mode features, sample data visibility, and training environment settings',
      value: manageScreenDataModes,
      setter: setManageScreenDataModes,
    },
  ];

  const performSave = async () => {
    setIsSaving(true);
    try {
      const permissionsPayload: UserRolePermissions = {
        permissions: Array.from(enabledPermissions),
        timesheetAccess: {
          viewAccess: viewTimesheetsAccess as 'none' | 'own' | 'team' | 'all',
          editAccess: editTimesheetsAccess as 'none' | 'own' | 'team' | 'all',
          allowDelete: allowDeleteTimesheets,
          allowDuplicate: allowDuplicateTimesheets,
          allowRecall: allowRecallTimesheet,
        },
        smartFeaturesPermissions: {
          enableCopyFromLastWeek,
          enableCopyDescription,
          enableCopyOnlyBillable,
          enableAutoSmartFill,
          enableAiInsights,
        },
        approvalPermissions: { viewDashboard: viewApprovalDashboard },
        timesheetApprovalPermissions: {
          viewApprovals,
          editTimesheets: editTimesheetsApproval,
          rejectTimesheets,
          compulsoryRejectionReason,
          bulkApprove,
          delegateApproval,
          viewPolicyViolations,
          viewDetectedIssues,
        },
        reportAccessPermissions: {
          timesheetReport: { enabled: enableTimesheetReport, viewAccess: viewTimesheetReportAccess, allowExport: allowTimesheetReportExport },
          missingTimesheetReport: { enabled: enableMissingTimesheetReport, viewAccess: viewMissingTimesheetReportAccess, allowExport: allowMissingTimesheetReportExport },
          utilisationReport: { enabled: enableUtilisationReport, viewAccess: viewUtilisationReportAccess, allowExport: allowUtilisationReportExport },
          // billableHoursReport removed — belongs in the Projects module of the PSA suite.
        },
        employeeDirectoryPermissions: { viewDirectory: viewEmployeeDirectory, viewAccess: viewEmployeeDirectoryAccess, viewDetails: viewEmployeeDetails },
        integrationsPermissions: { viewIntegrations, manageIntegrations },
        companyManagementPermissions: { viewCompanies, manageCompanies },
        systemSettingsPermissions: {
          manageGeneralSettings,
          manageDeadlines,
          managePolicySettings,
          manageUtilisationSettings,
          manageTimeEntrySettings,
          manageWorkTypeSettings,
          manageApprovalsSettings,
          manageNotifications,
          manageRolesPermissions: isSystem ? true : manageRolesPermissions,
          manageScreenDataModes,
        },
      };

      if (isCreateMode) {
        const createDto: CreateUserRoleDto = { name: roleName.trim(), description: roleDescription.trim(), isActive, permissions: permissionsPayload };
        await userRoleService.createUserRole(createDto);
      } else {
        const updateDto: UpdateUserRoleDto = { name: roleName.trim(), description: roleDescription.trim(), isActive, permissions: permissionsPayload };
        await userRoleService.updateUserRole(Number(roleId), updateDto);
      }

      window.dispatchEvent(new Event('rolesUpdated'));
      window.dispatchEvent(new Event('permissionsUpdated'));
      setIsSaving(false);
      setHasUnsavedChanges(false);
      setShowSaveConfirmation(false);

      toast.success(isCreateMode ? 'Role created successfully' : 'Role updated successfully', {
        description: `${roleName} has been ${isCreateMode ? 'created' : 'updated'} with ${getTotalPermissionsCount()} permissions.`,
      });

      if (onNavigate) onNavigate('/settings/roles-permissions');
      else navigate('/settings/roles-permissions');
    } catch (error: unknown) {
      console.error('Error saving role:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);

      // Detect duplicate role name errors from both the backend validation
      // and the SQL UNIQUE KEY constraint (in case the pre-check is bypassed)
      if (
        errorMessage.includes('already exists') ||
        errorMessage.includes('UQ_UserRoles') ||
        errorMessage.includes('UNIQUE KEY') ||
        errorMessage.includes('duplicate key')
      ) {
        setNameError(`A role with the name '${roleName.trim()}' already exists`);
        setActiveTab('details');
        toast.error('Duplicate role name', { description: `A role with the name '${roleName.trim()}' already exists. Please choose a different name.` });
      } else {
        toast.error('Save failed', { description: errorMessage || 'Failed to save role' });
      }
      setIsSaving(false);
    }
  };

  const handleSave = async () => {
    if (!roleName.trim()) { setNameError('Role name is required'); setActiveTab('details'); return; }
    if (roleName.trim().length < 2) { setNameError('Role name must be at least 2 characters'); setActiveTab('details'); return; }
    setNameError('');
    if (!isCreateMode) {
      setPermissionChanges(computePermissionDiff());
      setShowSaveConfirmation(true);
    } else {
      await performSave();
    }
  };

  /**
   * Apply the default permission template for a system role.
   * Only available for system roles (ids 1, 2, 3 = Administrator, User, Approver).
   * Populates all permission state from the DEFAULT_ROLE_PERMISSIONS_MAP constant.
   */
  const handleApplyDefaultTemplate = useCallback(() => {
    if (!roleId) return;
    const defaults = DEFAULT_ROLE_PERMISSIONS_MAP[Number(roleId)];
    if (!defaults) return;

    // Personal homepage + any other string-code permissions
    setEnabledPermissions(new Set(defaults.permissions ?? []));

    // Timesheet Access
    if (defaults.timesheetAccess) {
      setViewTimesheetsAccess(defaults.timesheetAccess.viewAccess);
      setEditTimesheetsAccess(defaults.timesheetAccess.editAccess);
      setAllowDeleteTimesheets(defaults.timesheetAccess.allowDelete);
      setAllowDuplicateTimesheets(defaults.timesheetAccess.allowDuplicate);
      setAllowRecallTimesheet(defaults.timesheetAccess.allowRecall);
    }

    // Smart Features
    if (defaults.smartFeaturesPermissions) {
      setEnableCopyFromLastWeek(defaults.smartFeaturesPermissions.enableCopyFromLastWeek);
      setEnableCopyDescription(defaults.smartFeaturesPermissions.enableCopyDescription);
      setEnableCopyOnlyBillable(defaults.smartFeaturesPermissions.enableCopyOnlyBillable);
      setEnableAutoSmartFill(defaults.smartFeaturesPermissions.enableAutoSmartFill);
      setEnableAiInsights(defaults.smartFeaturesPermissions.enableAiInsights);
    }

    // Approval Homepage
    setViewApprovalDashboard(defaults.approvalPermissions?.viewDashboard ?? false);

    // Timesheet Approval
    if (defaults.timesheetApprovalPermissions) {
      setViewApprovals(defaults.timesheetApprovalPermissions.viewApprovals);
      setEditTimesheetsApproval(defaults.timesheetApprovalPermissions.editTimesheets);
      setRejectTimesheets(defaults.timesheetApprovalPermissions.rejectTimesheets);
      setCompulsoryRejectionReason(defaults.timesheetApprovalPermissions.compulsoryRejectionReason);
      setBulkApprove(defaults.timesheetApprovalPermissions.bulkApprove);
      setDelegateApproval(defaults.timesheetApprovalPermissions.delegateApproval);
      setViewPolicyViolations(defaults.timesheetApprovalPermissions.viewPolicyViolations);
      setViewDetectedIssues(defaults.timesheetApprovalPermissions.viewDetectedIssues);
    }

    // Report Access
    if (defaults.reportAccessPermissions) {
      setEnableTimesheetReport(defaults.reportAccessPermissions.timesheetReport?.enabled ?? false);
      setViewTimesheetReportAccess(defaults.reportAccessPermissions.timesheetReport?.viewAccess ?? 'own');
      setAllowTimesheetReportExport(defaults.reportAccessPermissions.timesheetReport?.allowExport ?? false);
      setEnableMissingTimesheetReport(defaults.reportAccessPermissions.missingTimesheetReport?.enabled ?? false);
      setViewMissingTimesheetReportAccess(defaults.reportAccessPermissions.missingTimesheetReport?.viewAccess ?? 'own');
      setAllowMissingTimesheetReportExport(defaults.reportAccessPermissions.missingTimesheetReport?.allowExport ?? false);
      setEnableUtilisationReport(defaults.reportAccessPermissions.utilisationReport?.enabled ?? false);
      setViewUtilisationReportAccess(defaults.reportAccessPermissions.utilisationReport?.viewAccess ?? 'own');
      setAllowUtilisationReportExport(defaults.reportAccessPermissions.utilisationReport?.allowExport ?? false);
      // billableHoursReport removed — belongs in the Projects module of the PSA suite.
    }

    // Analytics section removed from Timesheet module — no defaults to set

    // Employee Directory
    if (defaults.employeeDirectoryPermissions) {
      setViewEmployeeDirectory(defaults.employeeDirectoryPermissions.viewDirectory);
      setViewEmployeeDirectoryAccess(defaults.employeeDirectoryPermissions?.viewAccess || 'own');
      setViewEmployeeDetails(defaults.employeeDirectoryPermissions.viewDetails);
    }

    // Integrations
    if (defaults.integrationsPermissions) {
      setViewIntegrations(defaults.integrationsPermissions.viewIntegrations);
      setManageIntegrations(defaults.integrationsPermissions.manageIntegrations);
    }

    // Company Management
    if (defaults.companyManagementPermissions) {
      setViewCompanies(defaults.companyManagementPermissions.viewCompanies);
      setManageCompanies(defaults.companyManagementPermissions.manageCompanies);
    }

    // System Settings
    if (defaults.systemSettingsPermissions) {
      setManageGeneralSettings(defaults.systemSettingsPermissions.manageGeneralSettings);
      setManageDeadlines(defaults.systemSettingsPermissions.manageDeadlines);
      setManagePolicySettings(defaults.systemSettingsPermissions.managePolicySettings);
      setManageUtilisationSettings(defaults.systemSettingsPermissions.manageUtilisationSettings);
      setManageTimeEntrySettings(defaults.systemSettingsPermissions.manageTimeEntrySettings ?? false);
      setManageWorkTypeSettings(defaults.systemSettingsPermissions.manageWorkTypeSettings ?? false);
      setManageApprovalsSettings(defaults.systemSettingsPermissions.manageApprovalsSettings);
      setManageNotifications(defaults.systemSettingsPermissions.manageNotifications);
      setManageRolesPermissions(defaults.systemSettingsPermissions.manageRolesPermissions);
      setManageScreenDataModes(defaults.systemSettingsPermissions.manageScreenDataModes);
    }

    setHasUnsavedChanges(true);
    toast.success('Default template applied. Review and save to confirm.');
  }, [roleId]);

  const handleTransferRole = (user: UserData) => {
    setSelectedUser(user);
    setNewRoleId('');
    setTransferDialogOpen(true);
  };

  const handleConfirmTransfer = async () => {
    if (!selectedUser || !newRoleId || !roleId) return;
    try {
      try {
        await userRoleService.transferEmployeeToRole(Number(roleId), Number(selectedUser.id), Number(newRoleId));
      } catch (transferError: unknown) {
        const is404 = (transferError instanceof Error && 'status' in transferError && (transferError as Record<string, unknown>).status === 404) || (transferError instanceof Error && (transferError.message.includes('not found') || transferError.message.includes('404')));
        if (!is404) throw transferError;
        // Atomic endpoint not deployed yet — fall back to remove + assign with rollback
        await userRoleService.removeEmployeeFromRole(Number(roleId), Number(selectedUser.id));
        try {
          await userRoleService.assignEmployeeToRole(Number(newRoleId), Number(selectedUser.id));
        } catch (assignError: unknown) {
          // Assign failed — rollback by re-adding to original role
          await userRoleService.assignEmployeeToRole(Number(roleId), Number(selectedUser.id));
          throw assignError;
        }
      }
      setUsers(prev => prev.filter(u => u.id !== selectedUser.id));
      window.dispatchEvent(new Event('rolesUpdated'));
      setTransferDialogOpen(false);
      setSelectedUser(null);
      setNewRoleId('');
    } catch (error: unknown) {
      toast.error('Transfer failed', { description: error instanceof Error ? error.message : 'Failed to transfer user' });
    }
  };

  // ── Bulk actions dropdown helper ──────────────────────────────────────────

  const BulkActionsDropdown: React.FC<{ onSelectAll: () => void; onDeselectAll: () => void }> = ({ onSelectAll, onDeselectAll }) => (
    <div className="flex items-center justify-end">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-2">
            <Settings2 className="h-4 w-4" />
            Bulk Actions
            <ChevronDown className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuItem onClick={onSelectAll}>
            <CheckSquare className="mr-2 h-4 w-4" />
            Select All
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onDeselectAll}>
            <Square className="mr-2 h-4 w-4" />
            Deselect All
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );

  // ── Render ────────────────────────────────────────────────────────────────

  // Permission gate — show friendly message when user lacks access
  if (!canManageRolesPermissions() || accessDenied) {
    return (
      <div className="space-y-6">
        <PageHeader
          title={isCreateMode ? 'Create New Role' : 'Edit Role'}
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Shield />}
        />
        <Alert>
          <Lock className="h-4 w-4" />
          <AlertDescription>
            You don't have permission to manage Roles & Permissions. Contact your administrator to request access.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24">

      {/* Page Header */}
      <PageHeader
        title={isCreateMode ? 'Create New Role' : (roleName || 'Edit Role')}
        subtitle={isCreateMode ? 'Set up a new role with specific permissions and access levels' : 'Configure role details and permissions'}
        size={36}
        showBreadcrumb={false}
      />

      {/* Unsaved Changes Alert */}
      {hasUnsavedChanges && (
        <Alert variant="warning" showIcon={false}>
          <AlertDescription>
            You have unsaved changes. Click "{isCreateMode ? 'Create Role' : 'Save Settings'}" at the bottom to apply your changes.
          </AlertDescription>
        </Alert>
      )}

      {/* Platform role info */}
      {isSystem && (
        <Alert showIcon={false}>
          <AlertDescription>
            <span className="font-medium">Platform Role</span> — You can modify permissions but cannot delete this role.
          </AlertDescription>
        </Alert>
      )}

      {/* Tabbed Interface */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-8 lg:w-auto lg:inline-flex">
          <TabsTrigger value="details" className="gap-2">
            <User className="h-4 w-4" />
            Details
          </TabsTrigger>
          <TabsTrigger value="time-tracking" className="gap-2">
            <Clock className="h-4 w-4" />
            Time Tracking
            {getTabPermissionCount('time-tracking') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('time-tracking')}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="approval" className="gap-2">
            <CheckSquare className="h-4 w-4" />
            Approval
            {getTabPermissionCount('approval') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('approval')}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reports" className="gap-2">
            <BarChart3 className="h-4 w-4" />
            Reports & Analytics
            {getTabPermissionCount('reports') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('reports')}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="management" className="gap-2">
            <User className="h-4 w-4" />
            Management
            {getTabPermissionCount('management') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('management')}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="data" className="gap-2">
            <FileText className="h-4 w-4" />
            Data & Integration
            {getTabPermissionCount('data') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('data')}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="administration" className="gap-2">
            <Settings className="h-4 w-4" />
            Administration
            {getTabPermissionCount('administration') > 0 && (
              <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{getTabPermissionCount('administration')}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="users" className="gap-2">
            <Users className="h-4 w-4" />
            Users
            <Badge variant="secondary" className="ml-1 h-5 min-w-5 px-1.5 text-xs">{users.length}</Badge>
          </TabsTrigger>
        </TabsList>

        {/* ── Details Tab ─────────────────────────────────────────────────────── */}
        <TabsContent value="details" className="space-y-4">
          <Accordion type="multiple" defaultValue={['role-details', 'role-summary']} className="space-y-3">

            {/* Role Details */}
            <SectionAccordionItem value="role-details" icon={<User className="h-5 w-5" />} title="Role Details" description="Configure the basic information for this role">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

                {/* Role Name */}
                <div className="flex flex-col gap-2 p-4 rounded-lg border bg-background">
                  <Label htmlFor="role-name" className="text-sm font-medium">
                    Role Name <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="role-name"
                    value={roleName}
                    onChange={(e) => {
                      const v = e.target.value;
                      setRoleName(v);
                      setHasUnsavedChanges(true);
                      if (!v.trim()) setNameError('Role name is required');
                      else if (v.trim().length < 2) setNameError('Role name must be at least 2 characters');
                      else setNameError('');
                    }}
                    placeholder="e.g., Project Manager"
                    disabled={isSystem}
                    className={nameError ? 'border-destructive' : ''}
                  />
                  {nameError
                    ? <p className="text-xs text-destructive">{nameError}</p>
                    : <p className="text-xs text-muted-foreground">{isSystem ? 'Platform role names cannot be changed' : 'The display name for this role'}</p>
                  }
                </div>

                {/* Active toggle */}
                <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                  <div className="flex-1 min-w-0">
                    <Label htmlFor="role-active" className="text-sm font-medium">Active</Label>
                    <p className="text-xs text-muted-foreground mt-0.5">Enable this role to make it available for assignment to users</p>
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span className="shrink-0">
                          <Checkbox
                            id="role-active"
                            checked={isActive}
                            onCheckedChange={(checked) => { setIsActive(!!checked); setHasUnsavedChanges(true); }}
                            disabled={isSystem}
                          />
                        </span>
                      </TooltipTrigger>
                      {isSystem && (
                        <TooltipContent side="top" align="end" className="max-w-[220px] text-center">
                          <p>Platform roles must remain active as they are required for system functionality</p>
                        </TooltipContent>
                      )}
                    </Tooltip>
                  </TooltipProvider>
                </div>

                {/* Description — spans full width */}
                <div className="flex flex-col gap-2 p-4 rounded-lg border bg-background lg:col-span-2">
                  <Label htmlFor="role-description" className="text-sm font-medium">Description</Label>
                  <Textarea
                    id="role-description"
                    value={roleDescription}
                    onChange={(e) => { setRoleDescription(e.target.value); setHasUnsavedChanges(true); }}
                    placeholder="Describe what this role can do..."
                    rows={3}
                  />
                  <p className="text-xs text-muted-foreground">A brief description of what this role can do</p>
                </div>
              </div>
            </SectionAccordionItem>

            {/* Summary */}
            <SectionAccordionItem value="role-summary" icon={<Shield className="h-5 w-5" />} title="Summary" description="Overview of enabled permissions across all categories">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
                <button
                  onClick={() => setActiveTab('users')}
                  className="rounded-lg border bg-background p-4 text-left hover:bg-accent hover:border-primary transition-all cursor-pointer group"
                >
                  <div className="flex items-center justify-between">
                    <p className="text-3xl font-bold">{users.length}</p>
                    <ArrowRight className="h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Users assigned to this role</p>
                  <p className="text-xs text-primary mt-2 opacity-0 group-hover:opacity-100 transition-opacity">Click to view users →</p>
                </button>
                <div className="rounded-lg border bg-background p-4">
                  <p className="text-3xl font-bold">{getTotalPermissionsCount()}</p>
                  <p className="text-sm text-muted-foreground mt-1">Total permissions enabled</p>
                </div>
                <div className="rounded-lg border bg-background p-4">
                  <div className="flex items-center gap-2">
                    <div className={`h-3 w-3 rounded-full ${isActive ? 'bg-success-500' : 'bg-neutral-400'}`} />
                    <p className="text-3xl font-bold">{isActive ? 'Active' : 'Inactive'}</p>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">Role status</p>
                </div>
              </div>
              <div className="p-4 rounded-lg border bg-background space-y-2">
                <p className="text-sm font-medium mb-2">Permissions by Category:</p>
                {permissionCategories.map((category) => {
                  const summaryCount = getCategorySummaryCount(category.id);
                  const percentage = summaryCount.total > 0 ? Math.round((summaryCount.enabled / summaryCount.total) * 100) : 0;
                  return (
                    <div key={category.id} className="flex items-center justify-between text-sm py-1.5 border-b last:border-0">
                      <div className="flex items-center gap-2">
                        {getIcon(category.iconName, 'h-4 w-4')}
                        <span>{category.name}</span>
                      </div>
                      <span className="text-muted-foreground">{summaryCount.enabled}/{summaryCount.total} ({percentage}%)</span>
                    </div>
                  );
                })}
              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Users Tab ───────────────────────────────────────────────────────── */}
        <TabsContent value="users" className="space-y-6">
          <div className="flex items-start gap-3 pb-4 border-b">
            <div className="rounded-lg bg-primary/10 p-2.5">
              <Users className="h-5 w-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold">Users Assigned to {roleName}</h3>
              <p className="text-sm text-muted-foreground mt-1">Manage users assigned to this role and transfer them to different roles</p>
            </div>
          </div>
          {users.length === 0 ? (
            <div className="flex items-center justify-center h-64 border rounded-lg bg-muted/20">
              <div className="text-center space-y-2">
                <Users className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No Users Assigned</p>
                <p className="text-sm text-muted-foreground">There are currently no users assigned to this role</p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-muted-foreground">{users.length} {users.length === 1 ? 'user' : 'users'} assigned to this role</p>
              <div className="border rounded-lg divide-y">
                {users.map((user) => (
                  <div key={user.id} className="p-4 hover:bg-muted/50 transition-colors">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-medium">{user.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Mail className="h-3 w-3 text-muted-foreground" />
                            <p className="text-sm text-muted-foreground">{user.email}</p>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>{user.status === 'active' ? 'Active' : 'Inactive'}</Badge>
                        <Button variant="outline" size="sm" onClick={() => handleTransferRole(user)}>Transfer Role</Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* ── Time Tracking Tab ───────────────────────────────────────────────── */}
        <TabsContent value="time-tracking" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllTimeTracking} onDeselectAll={handleDeselectAllTimeTracking} />
          <Accordion type="multiple" defaultValue={['personal-homepage', 'timesheet-access', 'smart-features']} className="space-y-3">

            <SectionAccordionItem value="personal-homepage" icon={<LayoutDashboard className="h-5 w-5" />} title="Personal Homepage" description="Access to personal homepage and metrics">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="dashboard.view_own" className="cursor-pointer">View Personal Homepage</Label>}
                  description="Access to personal homepage with dashboard and metrics"
                  control={
                    <Checkbox
                      id="dashboard.view_own"
                      checked={enabledPermissions.has('dashboard.view_own')}
                      onCheckedChange={(checked) => {
                        const newPerms = new Set(enabledPermissions);
                        if (checked) newPerms.add('dashboard.view_own'); else newPerms.delete('dashboard.view_own');
                        setEnabledPermissions(newPerms);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="dashboard.view_team" className="cursor-pointer">View Team Dashboard</Label>}
                  description="Access to team dashboard with team member metrics"
                  control={
                    <Checkbox
                      id="dashboard.view_team"
                      checked={enabledPermissions.has('dashboard.view_team')}
                      onCheckedChange={(checked) => {
                        const newPerms = new Set(enabledPermissions);
                        if (checked) newPerms.add('dashboard.view_team'); else newPerms.delete('dashboard.view_team');
                        setEnabledPermissions(newPerms);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="dashboard.view_department" className="cursor-pointer">View Department Dashboard</Label>}
                  description="Access to department-wide dashboard with aggregated metrics"
                  control={
                    <Checkbox
                      id="dashboard.view_department"
                      checked={enabledPermissions.has('dashboard.view_department')}
                      onCheckedChange={(checked) => {
                        const newPerms = new Set(enabledPermissions);
                        if (checked) newPerms.add('dashboard.view_department'); else newPerms.delete('dashboard.view_department');
                        setEnabledPermissions(newPerms);
                        setHasUnsavedChanges(true);
                      }}
                    />
                  }
                />
              </div>
            </SectionAccordionItem>

            <SectionAccordionItem value="timesheet-access" icon={<ClipboardList className="h-5 w-5" />} title="Timesheet Access" description="View, edit, and delete timesheet permissions">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="view-timesheets">View timesheets</Label>}
                  description="Access level for viewing timesheet entries"
                  control={
                    <Select value={viewTimesheetsAccess} onValueChange={(v) => { setViewTimesheetsAccess(v); setHasUnsavedChanges(true); }}>
                      <SelectTrigger id="view-timesheets" className="w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="own">Own only</SelectItem>
                        <SelectItem value="team">Team (department)</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="edit-timesheets">Edit timesheets</Label>}
                  description="Access level for modifying timesheet entries"
                  control={
                    <Select value={editTimesheetsAccess} onValueChange={(v) => { setEditTimesheetsAccess(v); setHasUnsavedChanges(true); }}>
                      <SelectTrigger id="edit-timesheets" className="w-44"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None</SelectItem>
                        <SelectItem value="own">Own only</SelectItem>
                        <SelectItem value="team">Team (department)</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="allow-delete" className="cursor-pointer">Allow Delete timesheets</Label>}
                  description="Allow deleting of timesheet lines"
                  onToggle={() => { setAllowDeleteTimesheets(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="allow-delete" checked={allowDeleteTimesheets} onCheckedChange={(c) => { setAllowDeleteTimesheets(c as boolean); setHasUnsavedChanges(true); }} />}
                />
                <PermissionCard
                  label={<Label htmlFor="allow-duplicate" className="cursor-pointer">Allow Duplicate timesheet lines</Label>}
                  description="Allow duplicate of timesheet lines"
                  onToggle={() => { setAllowDuplicateTimesheets(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="allow-duplicate" checked={allowDuplicateTimesheets} onCheckedChange={(c) => { setAllowDuplicateTimesheets(c as boolean); setHasUnsavedChanges(true); }} />}
                />
                <PermissionCard
                  label={<Label htmlFor="allow-recall" className="cursor-pointer">Recall Timesheet</Label>}
                  description="Allow recalling submitted timesheets for corrections"
                  onToggle={() => { setAllowRecallTimesheet(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="allow-recall" checked={allowRecallTimesheet} onCheckedChange={(c) => { setAllowRecallTimesheet(c as boolean); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>

            <SectionAccordionItem value="smart-features" icon={<Sparkles className="h-5 w-5" />} title="Smart Features Permissions" description="Control access to intelligent time entry features">
              <TooltipProvider>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                  {smartFeatureItems.map(({ id, label, description, enabled, value, setter }) => (
                    <PermissionCard
                      key={id}
                      label={<Label htmlFor={id} className={enabled ? 'cursor-pointer' : 'cursor-not-allowed text-muted-foreground'}>{label}</Label>}
                      description={description}
                      dimmed={!enabled}
                      onToggle={enabled ? () => { setter(!value); setHasUnsavedChanges(true); } : undefined}
                      control={
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div>
                              <Checkbox id={id} checked={value} disabled={!enabled} onCheckedChange={(c) => { setter(c as boolean); setHasUnsavedChanges(true); }} />
                            </div>
                          </TooltipTrigger>
                          {!enabled && <TooltipContent><p>This feature must be enabled in General Settings first</p></TooltipContent>}
                        </Tooltip>
                      }
                    />
                  ))}
                </div>
              </TooltipProvider>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Approval Tab ────────────────────────────────────────────────────── */}
        <TabsContent value="approval" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllApproval} onDeselectAll={handleDeselectAllApproval} />
          <Accordion type="multiple" defaultValue={['approval-homepage', 'timesheet-approval']} className="space-y-3">

            <SectionAccordionItem value="approval-homepage" icon={<CheckCircle2 className="h-5 w-5" />} title="Approval Homepage" description="Access to approval homepage and metrics">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="view-homepage" className="cursor-pointer">View Homepage</Label>}
                  description="View approval homepage with metrics and action items"
                  onToggle={() => { setViewApprovalDashboard(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="view-homepage" checked={viewApprovalDashboard} onCheckedChange={(c) => { setViewApprovalDashboard(c as boolean); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>

            <SectionAccordionItem value="timesheet-approval" icon={<ClipboardCheck className="h-5 w-5" />} title="Timesheet Approval" description="View and manage timesheet approvals">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-start">
                <PermissionCard
                  label={<Label htmlFor="view-approvals" className="cursor-pointer">View Approvals</Label>}
                  description="View timesheet approvals"
                  onToggle={() => { setViewApprovals(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-approvals"
                      checked={viewApprovals}
                      onCheckedChange={(c) => { setViewApprovals(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="edit-timesheets-approval" className="cursor-pointer">Edit Timesheets</Label>}
                  description="Modify time entries while reviewing for approval"
                  onToggle={() => { setEditTimesheetsApproval(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="edit-timesheets-approval"
                      checked={editTimesheetsApproval}
                      onCheckedChange={(c) => { setEditTimesheetsApproval(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="reject-timesheets" className="cursor-pointer">Reject Timesheets</Label>}
                    description="Reject timesheets"
                    onToggle={() => { setRejectTimesheets(v => { if (v) setCompulsoryRejectionReason(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="reject-timesheets"
                        checked={rejectTimesheets}
                        onCheckedChange={(c) => {
                          setRejectTimesheets(c as boolean);
                          if (!c) setCompulsoryRejectionReason(false);
                          setHasUnsavedChanges(true);
                        }}
                      />
                    }
                  />
                  {rejectTimesheets && (
                    <SubPermissionCard
                      label="Compulsory Rejection Reason"
                      description="Force reason for rejection"
                      onToggle={() => { setCompulsoryRejectionReason(v => !v); setHasUnsavedChanges(true); }}
                      control={
                        <Checkbox
                          id="compulsory-rejection"
                          checked={compulsoryRejectionReason}
                          onCheckedChange={(c) => { setCompulsoryRejectionReason(c as boolean); setHasUnsavedChanges(true); }}
                        />
                      }
                    />
                  )}
                </div>
                <PermissionCard
                  label={<Label htmlFor="bulk-approve" className="cursor-pointer">Bulk Approve & Reject</Label>}
                  description="Approve or reject multiple timesheets at once"
                  onToggle={() => { setBulkApprove(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="bulk-approve"
                      checked={bulkApprove}
                      onCheckedChange={(c) => { setBulkApprove(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="delegate-approval" className="cursor-pointer">Delegate Approval</Label>}
                  description="Allow this user to delegate their approval responsibilities to another user"
                  onToggle={() => { setDelegateApproval(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="delegate-approval"
                      checked={delegateApproval}
                      onCheckedChange={(c) => { setDelegateApproval(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-policy-violations" className="cursor-pointer">View Policy Violations</Label>}
                  description="View policy violations"
                  onToggle={() => { setViewPolicyViolations(v => !v); setHasUnsavedChanges(true); }}
                  control={
                    <Checkbox
                      id="view-policy-violations"
                      checked={viewPolicyViolations}
                      onCheckedChange={(c) => { setViewPolicyViolations(c as boolean); setHasUnsavedChanges(true); }}
                    />
                  }
                />
                <PermissionCard
                  label={<Label htmlFor="view-detected-issues" className="cursor-pointer">View Detected Issues</Label>}
                  description="View detected issues"
                  onToggle={() => { setViewDetectedIssues(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="view-detected-issues" checked={viewDetectedIssues} onCheckedChange={(c) => { setViewDetectedIssues(c as boolean); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Reports & Analytics Tab ─────────────────────────────────────────── */}
        <TabsContent value="reports" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllReports} onDeselectAll={handleDeselectAllReports} />
          <Accordion type="multiple" defaultValue={['report-access', 'analytics-insights']} className="space-y-3">

            <SectionAccordionItem value="report-access" icon={<FileBarChart className="h-5 w-5" />} title="Report Access" description="Control access to various reports and analytics">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

                {/* Timesheet Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-timesheet-report" className="cursor-pointer">Timesheet Report</Label>}
                    description="Access to detailed timesheet reports showing time entries, projects, and tasks"
                    onToggle={() => { setEnableTimesheetReport(v => { if (v) setAllowTimesheetReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-timesheet-report"
                        checked={enableTimesheetReport}
                        onCheckedChange={(c) => { setEnableTimesheetReport(c as boolean); if (!c) setAllowTimesheetReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableTimesheetReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Timesheet Report"
                        description="Define which timesheet data users can view in reports"
                        control={
                          <Select value={viewTimesheetReportAccess} onValueChange={(v) => { setViewTimesheetReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team (department)</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting timesheet report data to Excel or CSV"
                        onToggle={() => { setAllowTimesheetReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowTimesheetReportExport} onCheckedChange={(c) => { setAllowTimesheetReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* Missing Timesheet Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-missing-report" className="cursor-pointer">Missing Timesheet Report</Label>}
                    description="Access to reports showing employees with missing or incomplete timesheets"
                    onToggle={() => { setEnableMissingTimesheetReport(v => { if (v) setAllowMissingTimesheetReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-missing-report"
                        checked={enableMissingTimesheetReport}
                        onCheckedChange={(c) => { setEnableMissingTimesheetReport(c as boolean); if (!c) setAllowMissingTimesheetReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableMissingTimesheetReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Missing Timesheet Report"
                        description="Define which missing timesheet data users can view in reports"
                        control={
                          <Select value={viewMissingTimesheetReportAccess} onValueChange={(v) => { setViewMissingTimesheetReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team (department)</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting missing timesheet report data to Excel or CSV"
                        onToggle={() => { setAllowMissingTimesheetReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowMissingTimesheetReportExport} onCheckedChange={(c) => { setAllowMissingTimesheetReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* Utilisation Report */}
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="enable-utilisation-report" className="cursor-pointer">Utilisation Report</Label>}
                    description="Access to utilisation reports showing resource allocation and capacity metrics"
                    onToggle={() => { setEnableUtilisationReport(v => { if (v) setAllowUtilisationReportExport(false); return !v; }); setHasUnsavedChanges(true); }}
                    control={
                      <Checkbox
                        id="enable-utilisation-report"
                        checked={enableUtilisationReport}
                        onCheckedChange={(c) => { setEnableUtilisationReport(c as boolean); if (!c) setAllowUtilisationReportExport(false); setHasUnsavedChanges(true); }}
                      />
                    }
                  />
                  {enableUtilisationReport && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Utilisation Report"
                        description="Define which utilisation data users can view in reports"
                        control={
                          <Select value={viewUtilisationReportAccess} onValueChange={(v) => { setViewUtilisationReportAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team (department)</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                      <SubPermissionCard
                        label="Allow Export"
                        description="Allow exporting utilisation report data to Excel or CSV"
                        onToggle={() => { setAllowUtilisationReportExport(v => !v); setHasUnsavedChanges(true); }}
                        control={<Checkbox checked={allowUtilisationReportExport} onCheckedChange={(c) => { setAllowUtilisationReportExport(c as boolean); setHasUnsavedChanges(true); }} />}
                      />
                    </div>
                  )}
                </div>

                {/* billableHoursReport removed — belongs in the Projects module of the PSA suite. */}

              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Management Tab ──────────────────────────────────────────────────── */}
        <TabsContent value="management" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllManagement} onDeselectAll={handleDeselectAllManagement} />
          <Accordion type="multiple" defaultValue={['employee-directory']} className="space-y-3">
            <SectionAccordionItem value="employee-directory" icon={<Users className="h-5 w-5" />} title="Employee Directory" description="Manage access to employee information and profiles">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <div className="flex flex-col gap-2">
                  <PermissionCard
                    label={<Label htmlFor="view-employee-directory" className="cursor-pointer">View Employee Directory</Label>}
                    description="View the employee directory and basic information"
                    onToggle={() => { setViewEmployeeDirectory(v => !v); setHasUnsavedChanges(true); }}
                    control={<Checkbox id="view-employee-directory" checked={viewEmployeeDirectory} onCheckedChange={(c) => { setViewEmployeeDirectory(c as boolean); setHasUnsavedChanges(true); }} />}
                  />
                  {viewEmployeeDirectory && (
                    <div className="flex flex-col gap-2">
                      <SubPermissionCard
                        label="View Members"
                        description="Define which team members this role can see in the directory"
                        control={
                          <Select value={viewEmployeeDirectoryAccess} onValueChange={(v) => { setViewEmployeeDirectoryAccess(v); setHasUnsavedChanges(true); }}>
                            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="own">Own Only</SelectItem>
                              <SelectItem value="team">Team (same department)</SelectItem>
                              <SelectItem value="all">All</SelectItem>
                            </SelectContent>
                          </Select>
                        }
                      />
                    </div>
                  )}
                </div>
                <PermissionCard
                  label={<Label htmlFor="view-employee-details" className="cursor-pointer">View Employee Details</Label>}
                  description="View detailed employee information and profiles"
                  onToggle={() => { setViewEmployeeDetails(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="view-employee-details" checked={viewEmployeeDetails} onCheckedChange={(c) => { setViewEmployeeDetails(c as boolean); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Data & Integration Tab ──────────────────────────────────────────── */}
        <TabsContent value="data" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllDataIntegration} onDeselectAll={handleDeselectAllDataIntegration} />
          <Accordion type="multiple" defaultValue={['integrations', 'company-management']} className="space-y-3">

            <SectionAccordionItem value="integrations" icon={<Plug className="h-5 w-5" />} title="Integrations" description="Manage access to system integrations and data synchronisation">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="view-integrations" className="cursor-pointer">View Integrations</Label>}
                  description="View integration configurations and status"
                  onToggle={() => { setViewIntegrations(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="view-integrations" checked={viewIntegrations} onCheckedChange={(c) => { setViewIntegrations(c as boolean); setHasUnsavedChanges(true); }} />}
                />
                <PermissionCard
                  label={<Label htmlFor="manage-integrations" className="cursor-pointer">Manage Integrations</Label>}
                  description="Configure, connect, and disconnect system integrations"
                  onToggle={() => { setManageIntegrations(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="manage-integrations" checked={manageIntegrations} onCheckedChange={(c) => { setManageIntegrations(c as boolean); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>

            <SectionAccordionItem value="company-management" icon={<Building2 className="h-5 w-5" />} title="Company Management" description="Manage access to company information and settings">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <PermissionCard
                  label={<Label htmlFor="view-companies" className="cursor-pointer">View Companies</Label>}
                  description="View company list and basic information"
                  onToggle={() => { setViewCompanies(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="view-companies" checked={viewCompanies} onCheckedChange={(c) => { setViewCompanies(c as boolean); setHasUnsavedChanges(true); }} />}
                />
                <PermissionCard
                  label={<Label htmlFor="manage-companies" className="cursor-pointer">Manage Companies</Label>}
                  description="Create, edit, and manage company settings"
                  onToggle={() => { setManageCompanies(v => !v); setHasUnsavedChanges(true); }}
                  control={<Checkbox id="manage-companies" checked={manageCompanies} onCheckedChange={(c) => { setManageCompanies(c as boolean); setHasUnsavedChanges(true); }} />}
                />
              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>

        {/* ── Administration Tab ──────────────────────────────────────────────── */}
        <TabsContent value="administration" className="space-y-4">
          <BulkActionsDropdown onSelectAll={handleSelectAllAdministration} onDeselectAll={handleDeselectAllAdministration} />
          <Accordion type="multiple" defaultValue={['system-settings']} className="space-y-3">
            <SectionAccordionItem
              value="system-settings"
              icon={<Settings className="h-5 w-5" />}
              title="System Settings"
              description="Control access to system-wide configuration and administrative functions"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

                {/* General Settings — spans full width as it has a Select */}
                <div className="lg:col-span-2">
                  <PermissionCard
                    label={<Label htmlFor="manage-general-settings">Manage General Settings</Label>}
                    description="View or modify core system configuration including company details, branding, and default preferences"
                    control={
                      <Select
                        value={manageGeneralSettings}
                        onValueChange={(value: 'view' | 'configure') => {
                          setManageGeneralSettings(value);
                          if (value === 'view') {
                            setManageDeadlines(false);
                            setManagePolicySettings(false); setManageUtilisationSettings(false);
                            setManageTimeEntrySettings(false); setManageWorkTypeSettings(false);
                            setManageApprovalsSettings(false); setManageNotifications(false);
                            setManageRolesPermissions(false); setManageScreenDataModes(false);
                          }
                          setHasUnsavedChanges(true);
                        }}
                      >
                        <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="view">View Only</SelectItem>
                          <SelectItem value="configure">Configure</SelectItem>
                        </SelectContent>
                      </Select>
                    }
                  />
                </div>

                {/* All sub-settings require Configure access */}
                {adminSubSettings.map(({ id, label, description, value, setter }) => (
                  <TooltipProvider key={id}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div>
                          <PermissionCard
                            label={<Label htmlFor={id} className={manageGeneralSettings === 'view' ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}>{label}</Label>}
                            description={description}
                            dimmed={manageGeneralSettings === 'view'}
                            control={
                              <Checkbox
                                id={id}
                                checked={value}
                                disabled={manageGeneralSettings === 'view'}
                                onCheckedChange={(c) => { setter(c as boolean); setHasUnsavedChanges(true); }}
                              />
                            }
                          />
                        </div>
                      </TooltipTrigger>
                      {manageGeneralSettings === 'view' && <TooltipContent><p>Requires 'Configure' access to General Settings</p></TooltipContent>}
                    </Tooltip>
                  </TooltipProvider>
                ))}

                {/* Manage Roles & Permissions — special case: locked for system roles */}
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div>
                        <PermissionCard
                          label={
                            <Label
                              htmlFor="manage-roles-permissions"
                              className={(manageGeneralSettings === 'view' || isSystem) ? 'cursor-not-allowed text-muted-foreground' : 'cursor-pointer'}
                            >
                              Manage Roles & Permissions
                            </Label>
                          }
                          description="Create custom user roles, assign permission levels, and control what different user types can access and modify"
                          dimmed={manageGeneralSettings === 'view' || isSystem}
                          control={
                            <Checkbox
                              id="manage-roles-permissions"
                              checked={isSystem ? true : manageRolesPermissions}
                              disabled={manageGeneralSettings === 'view' || isSystem}
                              onCheckedChange={(c) => { setManageRolesPermissions(c as boolean); setHasUnsavedChanges(true); }}
                            />
                          }
                        />
                      </div>
                    </TooltipTrigger>
                    {isSystem
                      ? <TooltipContent><p>This permission cannot be disabled on the Administrator role</p></TooltipContent>
                      : manageGeneralSettings === 'view'
                        ? <TooltipContent><p>Requires 'Configure' access to General Settings</p></TooltipContent>
                        : null
                    }
                  </Tooltip>
                </TooltipProvider>
              </div>
            </SectionAccordionItem>
          </Accordion>
        </TabsContent>
      </Tabs>

      {/* Save Confirmation Dialog */}
      <Dialog open={showSaveConfirmation} onOpenChange={setShowSaveConfirmation}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm Role Changes</DialogTitle>
            <DialogDescription>Review the role configuration before saving</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Role:</span>
                <span className="text-sm">{roleName}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Users affected:</span>
                <span className="text-sm">{users.length} {users.length === 1 ? 'user' : 'users'}</span>
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm font-medium">Total permissions enabled:</span>
                <span className="text-sm font-semibold text-primary">{getTotalPermissionsCount()}</span>
              </div>
            </div>
            {(permissionChanges.added.length > 0 || permissionChanges.removed.length > 0) && (
              <>
                <Separator />
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {permissionChanges.added.map((label) => (
                    <div key={`add-${label}`} className="flex items-center gap-2 text-sm text-success-700 dark:text-success-400">
                      <Plus className="h-4 w-4 shrink-0" />
                      <span>{label}</span>
                    </div>
                  ))}
                  {permissionChanges.removed.map((label) => (
                    <div key={`rm-${label}`} className="flex items-center gap-2 text-sm text-destructive dark:text-red-400">
                      <Minus className="h-4 w-4 shrink-0" />
                      <span>{label}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
            <Separator />
            <div className="rounded-lg bg-blue-50 dark:bg-blue-950/20 p-3 border border-blue-200 dark:border-blue-800">
              <div className="flex gap-2">
                <AlertCircle className="h-5 w-5 text-blue-600 dark:text-blue-400 shrink-0 mt-0.5" />
                <p className="text-sm text-blue-900 dark:text-blue-100">
                  These changes will be applied to all {users.length} {users.length === 1 ? 'user' : 'users'} with this role.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSaveConfirmation(false)}>Cancel</Button>
            <Button onClick={performSave} disabled={isSaving}>
              {isSaving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving...</> : 'Confirm & Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Transfer Role Dialog */}
      <Dialog open={transferDialogOpen} onOpenChange={setTransferDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Transfer User to Different Role</DialogTitle>
            <DialogDescription>Select a new role for {selectedUser?.name}. This will immediately update their permissions.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Role</Label>
              <div className="p-3 rounded-lg border bg-muted/50"><p className="font-medium">{roleName}</p></div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="new-role">New Role</Label>
              <Select value={newRoleId} onValueChange={setNewRoleId}>
                <SelectTrigger id="new-role"><SelectValue placeholder="Select a role" /></SelectTrigger>
                <SelectContent>
                  {allRoles.filter(role => String(role.id) !== roleId).map(role => (
                    <SelectItem key={role.id} value={String(role.id)}>
                      <div className="flex items-center gap-2">
                        <span>{role.name}</span>
                        {role.isSystem && <Badge variant="secondary" className="text-xs">Platform</Badge>}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {selectedUser && (
              <div className="p-3 rounded-lg border bg-info-50 dark:bg-info-950/20">
                <p className="text-sm text-muted-foreground">
                  <strong>{selectedUser.name}</strong> will be transferred from <strong>{roleName}</strong> to the selected role.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTransferDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleConfirmTransfer} disabled={!newRoleId}>Transfer User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sticky Footer */}
      <div className="fixed bottom-0 z-10 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60" style={{ left: 'var(--sidebar-width)', right: 0 }}>
        <div className="flex h-16 items-center justify-end gap-3 px-6 max-w-[2000px] mx-auto">
          <Button variant="outline" size="lg" className="min-w-[120px]" onClick={handleCancel}>Cancel</Button>
          <Button size="lg" className="min-w-[120px]" onClick={handleSave} disabled={isCreateMode ? false : !hasUnsavedChanges}>
            {isCreateMode ? 'Create Role' : 'Save Settings'}
          </Button>
        </div>
      </div>
    </div>
  );
};
